import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Container, Modal, Row, Spinner } from 'react-bootstrap';
import QRCode from 'qrcode';
import { useParams } from 'react-router-dom';
import { buyerLocationService } from '../../services/buyerLocationService';
import {
  PublicBuyerContext,
  PublicCheckoutOption,
  PublicPaymentPage as PublicPaymentPageDto,
  PublicPaymentStatus,
  SimpleSwapCurrencyResult,
  publicPaymentService,
} from '../../services/publicPaymentService';
import methodCreditCardIcon from '../../assets/payment-page/methods/creditcard.svg';
import {
  isSuccessFinalStatus,
  isUnderpaidDisplayStatus,
  shouldShowMerchantReturnButton,
} from './publicPaymentStatusGuards';
import './PublicPaymentPage.css';

const FINAL_STATUSES = new Set(['Completed', 'PayoutCompleted', 'Failed', 'Expired']);
const STATUS_POLL_INTERVAL_MS = 5000;
const RECEIVED_SETTLING_STATUS = 'Payment received (Settling)';
const SWAP_UNDERPAYMENT_REVIEW_STATUS = 'Swap Underpayment (Review Required)';
const MINIMUM_ON_CHAIN_COMPLETION_RATIO = 0.995;
const CRYPTO_AMOUNT_EPSILON = 0.0000005;
const RIZZPAY_LOGO_PUBLIC_PATH = '/rizzpay-mark.png';
const RIZZPAY_TAGLINE = 'Unchain Your Payments';
const USDC_ICON_URL = 'https://static.simpleswap.io/images/currencies-logo/usdc.svg';

type FlowKind = 'menu' | 'crypto' | 'direct' | 'fiat' | 'swap';
type ProgressStepState = 'complete' | 'active' | 'pending';

type PaymentProgressStep = {
  key: string;
  label: string;
  description: string;
  state: ProgressStepState;
};

type SimpleSwapExchange = {
  publicId: string | null;
  status: string | null;
  redirectUrl: string | null;
  depositAddress: string | null;
  depositAmount: string | null;
  depositTicker: string | null;
  depositNetwork: string | null;
};

const createEmptySwapExchange = (): SimpleSwapExchange => ({
  publicId: null,
  status: null,
  redirectUrl: null,
  depositAddress: null,
  depositAmount: null,
  depositTicker: null,
  depositNetwork: null,
});

type SimpleSwapCurrencyOption = {
  symbol: string;
  network: string;
  displayName: string;
  key: string;
  imageUrl: string | null;
};

type SwapAssetNetworkOption = {
  symbol: string;
  network: string;
  label: string;
  displayName: string;
  networkLabel: string;
  imageUrl: string | null;
  iconUrl: string | null;
  key: string;
};

const simpleSwapNetworkAliases: Record<string, string> = {
  polygon: 'matic',
  matic: 'matic',
  ethereum: 'eth',
  erc20: 'eth',
  ethereummainnet: 'eth',
  binancesmartchain: 'bsc',
  bsc: 'bsc',
  avalanchec: 'avaxc',
  avaxc: 'avaxc',
  tron: 'trx',
  solana: 'sol',
  arbitrumone: 'arbitrum',
};

const popularSwapSymbols = ['btc', 'eth', 'usdt', 'usdc', 'sol', 'bnb', 'trx', 'xrp', 'ltc', 'doge'];
const popularSwapNetworks = ['btc', 'eth', 'trx', 'bsc', 'matic', 'sol', 'xrp', 'ltc', 'doge', 'arbitrum'];

const preferredSwapNetworksBySymbol: Record<string, string[]> = {
  btc: ['btc'],
  eth: ['eth', 'arbitrum'],
  usdt: ['trx', 'eth', 'bsc', 'matic'],
  usdc: ['matic', 'eth', 'bsc', 'arbitrum'],
  sol: ['sol'],
  bnb: ['bsc'],
  trx: ['trx'],
  xrp: ['xrp'],
  ltc: ['ltc'],
  doge: ['doge'],
};

const simpleSwapNetworkIconAliases: Record<string, string> = {
  lightning: 'btc',
  lineaaeth: 'linea',
  lineaeth: 'linea',
};

const normalizeKey = (value?: string | null) =>
  (value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

const normalizeSimpleSwapNetwork = (value?: string | null): string => {
  const normalized = normalizeKey(value);
  if (!normalized) {
    return '';
  }

  return simpleSwapNetworkAliases[normalized] || normalized;
};

const resolveSimpleSwapNetworkIconUrl = (network?: string | null): string | null => {
  const normalized = normalizeSimpleSwapNetwork(network);
  if (!normalized) {
    return null;
  }

  const iconKey = simpleSwapNetworkIconAliases[normalized] || normalized;
  return `https://static.simpleswap.io/images/currencies-logo/${iconKey}.svg`;
};

const getPopularSymbolRank = (symbol?: string | null): number => {
  const index = popularSwapSymbols.indexOf(normalizeKey(symbol));
  return index === -1 ? 999 : index;
};

const getPopularNetworkRank = (network?: string | null): number => {
  const index = popularSwapNetworks.indexOf(normalizeSimpleSwapNetwork(network));
  return index === -1 ? 999 : index;
};

const compareSwapAssetNetworkOptions = (left: SwapAssetNetworkOption, right: SwapAssetNetworkOption): number =>
  getPopularSymbolRank(left.symbol) - getPopularSymbolRank(right.symbol)
  || left.symbol.localeCompare(right.symbol)
  || getPopularNetworkRank(left.network) - getPopularNetworkRank(right.network)
  || left.network.localeCompare(right.network);

const readString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const parseSourceAmountFromEstimate = (result?: Record<string, unknown>): string => {
  const candidates: unknown[] = [
    result?.amountFrom,
    result?.fromAmount,
    result?.sourceAmount,
    result?.amount,
    result?.estimatedAmount,
    result?.expectedAmountFrom,
    result?.youSend,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
      return `${candidate}`;
    }

    if (typeof candidate === 'string') {
      const normalized = candidate.trim().replace(',', '.');
      const parsed = Number(normalized);
      if (Number.isFinite(parsed) && parsed > 0) {
        return normalized;
      }
    }
  }

  return '';
};

const parseUnknownString = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed ? trimmed : null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return `${value}`;
  }

  return null;
};

const toPlainDecimalString = (value?: string | number | null): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value) || value <= 0) {
      return null;
    }

    return value
      .toFixed(18)
      .replace(/0+$/g, '')
      .replace(/\.$/, '');
  }

  const normalized = value.trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(normalized)) {
    return null;
  }

  if (Number(normalized) <= 0) {
    return null;
  }

  return normalized;
};

const normalizeAddress = (value?: string | null): string | null => {
  const address = value?.trim();
  return address ? address : null;
};

const buildDirectUsdcQrPayload = (page: PublicPaymentPageDto): string => {
  const address = normalizeAddress(page.uniqueWalletAddress);
  return address || '';
};

const buildSwapDepositQrPayload = (
  address?: string | null,
  amount?: string | number | null,
  ticker?: string | null,
  network?: string | null
): string => {
  const depositAddress = normalizeAddress(address);
  if (!depositAddress) {
    return '';
  }

  const paymentAmount = toPlainDecimalString(amount);
  const symbol = normalizeKey(ticker);
  const normalizedNetwork = normalizeSimpleSwapNetwork(network);

  if (!paymentAmount || !symbol) {
    return depositAddress;
  }

  const encodedAddress = encodeURIComponent(depositAddress);
  const encodedAmount = encodeURIComponent(paymentAmount);

  if (symbol === 'btc' && normalizedNetwork === 'btc') {
    return `bitcoin:${encodedAddress}?amount=${encodedAmount}`;
  }

  if (symbol === 'ltc' && normalizedNetwork === 'ltc') {
    return `litecoin:${encodedAddress}?amount=${encodedAmount}`;
  }

  if (symbol === 'doge' && normalizedNetwork === 'doge') {
    return `dogecoin:${encodedAddress}?amount=${encodedAmount}`;
  }

  if ((symbol === 'bch' || symbol === 'bcha') && (normalizedNetwork === 'bch' || normalizedNetwork === 'bcha')) {
    return `bitcoincash:${encodedAddress}?amount=${encodedAmount}`;
  }

  if (symbol === 'sol' && normalizedNetwork === 'sol') {
    return `solana:${encodedAddress}?amount=${encodedAmount}`;
  }

  return depositAddress;
};

const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const normalizeSimpleSwapStatus = (status?: string | null): string => normalizeKey(status);

const isSimpleSwapAwaitingDepositStatus = (status?: string | null): boolean => {
  const normalized = normalizeSimpleSwapStatus(status);
  return normalized === '' ||
    normalized === 'waiting' ||
    normalized === 'waitingdeposit' ||
    normalized === 'created' ||
    normalized === 'new' ||
    normalized === 'pending' ||
    normalized === 'awaitingdeposit';
};

const isSimpleSwapFailedStatus = (status?: string | null): boolean => {
  const normalized = normalizeSimpleSwapStatus(status);
  return normalized === 'failed' ||
    normalized === 'refunded' ||
    normalized === 'expired' ||
    normalized === 'cancelled';
};

const isSimpleSwapSubmittedStatus = (status?: string | null): boolean => {
  const normalized = normalizeSimpleSwapStatus(status);
  if (!normalized) {
    return false;
  }

  if (isSimpleSwapAwaitingDepositStatus(normalized) || isSimpleSwapFailedStatus(normalized)) {
    return false;
  }

  return true;
};

const resolveSimpleSwapProgressIndex = (status?: string | null, isSuccessFinal = false): number => {
  if (isSuccessFinal) {
    return 2;
  }

  const normalized = normalizeSimpleSwapStatus(status);
  if (isSimpleSwapAwaitingDepositStatus(normalized)) {
    return -1;
  }

  if (normalized.includes('confirm') || normalized.includes('deposit') || normalized.includes('received')) {
    return 0;
  }

  if (normalized.includes('exchange') || normalized.includes('swap')) {
    return 1;
  }

  if (normalized.includes('send') || normalized.includes('withdraw') || normalized.includes('payout')) {
    return 1;
  }

  if (normalized.includes('finish') || normalized.includes('complete') || normalized.includes('success')) {
    return 2;
  }

  return normalized ? 0 : -1;
};

const resolveDirectProgressIndex = (
  status?: string | null,
  displayStatus?: string | null,
  hasEvidence = false,
  isSuccessFinal = false
): number => {
  if (isSuccessFinal) {
    return 1;
  }

  if ((displayStatus || '') === RECEIVED_SETTLING_STATUS) {
    return 0;
  }

  const normalizedStatus = normalizeKey(status);
  if (normalizedStatus === 'pending') {
    return 0;
  }

  return hasEvidence ? 0 : -1;
};

const applyProgressState = (
  steps: Array<Omit<PaymentProgressStep, 'state'>>,
  activeIndex: number
): PaymentProgressStep[] =>
  steps.map((step, index) => ({
    ...step,
    state: index < activeIndex ? 'complete' : index === activeIndex ? 'active' : 'pending',
  }));

const buildDirectProgressSteps = (
  status?: string | null,
  displayStatus?: string | null,
  hasEvidence = false,
  isSuccessFinal = false
): PaymentProgressStep[] =>
  applyProgressState(
    [
      {
        key: 'detected',
        label: 'Payment detected',
        description: 'We found your transfer and are checking confirmations.',
      },
      {
        key: 'complete',
        label: 'Payment approved',
        description: 'The business receives the USDC settlement.',
      },
    ],
    resolveDirectProgressIndex(status, displayStatus, hasEvidence, isSuccessFinal)
  );

const buildSwapProgressSteps = (
  providerStatus?: string | null,
  isSuccessFinal = false
): PaymentProgressStep[] =>
  applyProgressState(
    [
      {
        key: 'detected',
        label: 'Payment detected',
        description: 'RizzSwap detected your incoming payment.',
      },
      {
        key: 'swapping',
        label: 'Payment converted',
        description: 'The provider is converting your asset into USDC.',
      },
      {
        key: 'complete',
        label: 'Payment approved',
        description: 'The business receives the USDC settlement.',
      },
    ],
    resolveSimpleSwapProgressIndex(providerStatus, isSuccessFinal)
  );

const PaymentProgressTimeline = ({ steps }: { steps: PaymentProgressStep[] }) => (
  <div
    className="pp-payment-progress"
    aria-label="Payment progress"
    style={{ '--pp-progress-step-count': steps.length } as React.CSSProperties}
  >
    {steps.map((step, index) => (
      <div
        key={step.key}
        className={`pp-payment-progress__step pp-payment-progress__step--${step.state}`}
      >
        <div className="pp-payment-progress__marker" aria-hidden="true">
          {step.state === 'complete' ? '✓' : index + 1}
        </div>
        <div className="pp-payment-progress__copy">
          <strong>{step.label}</strong>
          <span>{step.description}</span>
        </div>
      </div>
    ))}
  </div>
);

const formatAmount = (amount: number, currency: string): string =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: ['USDC', 'USDT'].includes(currency.toUpperCase()) ? 6 : 2,
  }).format(amount);

const formatClipboardAmount = (amount: number, currency: string): string =>
  new Intl.NumberFormat('en-US', {
    useGrouping: false,
    minimumFractionDigits: 0,
    maximumFractionDigits: ['USDC', 'USDT'].includes(currency.toUpperCase()) ? 6 : 8,
  }).format(amount);

const formatDateTime = (value?: string | null): string =>
  value ? new Date(value).toLocaleString() : 'Not available';

const resolveFlowFromPaymentSource = (paymentSource?: string | null): FlowKind => {
  const normalized = (paymentSource || '').toLowerCase();
  if (normalized.includes('fiat')) {
    return 'fiat';
  }

  if (normalized.includes('swap')) {
    return 'swap';
  }

  if (normalized.includes('direct')) {
    return 'direct';
  }

  return 'direct';
};

const isSubmittedStatus = (status: string, displayStatus: string): boolean =>
  displayStatus === RECEIVED_SETTLING_STATUS || status === 'Pending';

const hasPositiveAmount = (value?: number | null): boolean =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

const hasSubmissionEvidence = (
  currentStatus: PublicPaymentStatus | null,
  currentPage: PublicPaymentPageDto | null
): boolean => {
  const paidAt = currentStatus?.paidAt ?? currentPage?.paidAt;
  const txHash = currentStatus?.txHash ?? currentPage?.txHash;
  const settlementTxHash = currentStatus?.settlementTxHash ?? currentPage?.settlementTxHash;
  const cryptoAmount = currentStatus?.cryptoAmount ?? currentPage?.cryptoAmount;
  const receivedCryptoAmount = currentStatus?.receivedCryptoAmount ?? currentPage?.receivedCryptoAmount;
  const distributedCryptoAmount = currentStatus?.distributedCryptoAmount ?? currentPage?.distributedCryptoAmount;

  return Boolean(paidAt || txHash || settlementTxHash)
    || hasPositiveAmount(cryptoAmount)
    || hasPositiveAmount(receivedCryptoAmount)
    || hasPositiveAmount(distributedCryptoAmount);
};

const chooseProviderOptions = (options: PublicCheckoutOption[]): PublicCheckoutOption[] => {
  const enabled = options.filter(option => option.isEnabled);
  const map = new Map<string, PublicCheckoutOption>();

  enabled.forEach(option => {
    const key = normalizeKey(option.provider) || option.sessionId;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, option);
      return;
    }

    const existingMethod = normalizeKey(existing.paymentMethod);
    const candidateMethod = normalizeKey(option.paymentMethod);
    const existingCardLike = existingMethod.includes('card');
    const candidateCardLike = candidateMethod.includes('card');

    if (!existing.isRecommended && option.isRecommended) {
      map.set(key, option);
      return;
    }

    if (!existingCardLike && candidateCardLike) {
      map.set(key, option);
    }
  });

  return Array.from(map.values()).sort((left, right) => {
    if (left.isRecommended !== right.isRecommended) {
      return left.isRecommended ? -1 : 1;
    }

    return left.provider.localeCompare(right.provider);
  });
};

const openCheckoutWindow = (url?: string | null): Window | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const popup = url
    ? window.open(url, '_blank', 'noopener,noreferrer')
    : window.open('', '_blank');
  if (!popup) {
    return null;
  }

  try {
    popup.opener = null;
  } catch {
    // no-op
  }

  return popup;
};

const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  const maybeError = error as {
    response?: {
      data?: {
        error?: string;
        message?: string;
        title?: string;
        expandedError?: {
          message?: string;
        };
      };
      status?: number;
    };
    message?: string;
  };

  if (maybeError?.response?.data?.error) {
    return maybeError.response.data.error;
  }

  if (maybeError?.response?.data?.message) {
    return maybeError.response.data.message;
  }

  if (maybeError?.response?.data?.expandedError?.message) {
    return maybeError.response.data.expandedError.message;
  }

  if (maybeError?.response?.data?.title) {
    return maybeError.response.data.title;
  }

  if (maybeError?.message) {
    return maybeError.message;
  }

  return fallback;
};

const shouldRefreshProviderOptionsAfterError = (message: string): boolean => {
  const normalized = message.trim().toLowerCase();
  if (!normalized) {
    return false;
  }

  return normalized.includes('temporarily unavailable')
    || normalized.includes('choose another provider')
    || normalized.includes('unable to reach the provider')
    || normalized.includes('checkout session not found');
};

const PublicPaymentPageView: React.FC = () => {
  const { uniqueHash } = useParams<{ uniqueHash: string }>();
  const [paymentPage, setPaymentPage] = useState<PublicPaymentPageDto | null>(null);
  const [status, setStatus] = useState<PublicPaymentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFlow, setActiveFlow] = useState<FlowKind>('menu');
  const [buyerContext, setBuyerContext] = useState<PublicBuyerContext | null>(null);
  const [fiatOptions, setFiatOptions] = useState<PublicCheckoutOption[]>([]);
  const [fiatOptionsLoading, setFiatOptionsLoading] = useState(false);
  const [selectedFiatSessionId, setSelectedFiatSessionId] = useState<string | null>(null);
  const [lastAttemptedFiatProvider, setLastAttemptedFiatProvider] = useState<PublicCheckoutOption | null>(null);
  const [fiatLaunchInProgress, setFiatLaunchInProgress] = useState(false);
  const [fiatProviderIssueSubmitting, setFiatProviderIssueSubmitting] = useState(false);
  const [fiatError, setFiatError] = useState<string | null>(null);
  const [swapFromTicker, setSwapFromTicker] = useState('');
  const [swapFromNetwork, setSwapFromNetwork] = useState('');
  const [swapAmount, setSwapAmount] = useState('');
  const [swapCurrencies, setSwapCurrencies] = useState<SimpleSwapCurrencyOption[]>([]);
  const [swapCurrenciesLoading, setSwapCurrenciesLoading] = useState(false);
  const [swapEstimating, setSwapEstimating] = useState(false);
  const [swapCreating, setSwapCreating] = useState(false);
  const [swapError, setSwapError] = useState<string | null>(null);
  const [swapRateId, setSwapRateId] = useState<string | null>(null);
  const [swapExchange, setSwapExchange] = useState<SimpleSwapExchange>(() => createEmptySwapExchange());
  const [swapDepositQrCodeDataUrl, setSwapDepositQrCodeDataUrl] = useState<string | null>(null);
  const [swapTickerPickerOpen, setSwapTickerPickerOpen] = useState(false);
  const [swapTickerSearch, setSwapTickerSearch] = useState('');
  const [swapRouteNetworkFilter, setSwapRouteNetworkFilter] = useState('all');
  const [swapNetworkPickerOpen, setSwapNetworkPickerOpen] = useState(false);
  const [copiedSwapDepositAddress, setCopiedSwapDepositAddress] = useState(false);
  const [copiedWallet, setCopiedWallet] = useState(false);
  const [copiedTopUpAmount, setCopiedTopUpAmount] = useState(false);
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string | null>(null);
  const [clock, setClock] = useState(() => Date.now());
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const isEmbedded = useMemo(() => {
    if (typeof window === 'undefined') {
      return false;
    }

    try {
      return window.self !== window.top;
    } catch {
      return true;
    }
  }, []);

  useEffect(() => {
    if (!uniqueHash) {
      setError('Payment page not found.');
      setLoading(false);
      return;
    }

    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        const page = await publicPaymentService.getPaymentPage(uniqueHash);
        if (!active) {
          return;
        }

        setPaymentPage(page);
        setStatus({
          status: page.status,
          lifecycleStatus: page.lifecycleStatus,
          displayStatus: page.displayStatus,
          paymentSource: page.paymentSource,
          provider: page.provider,
          cryptoAmount: page.cryptoAmount,
          receivedCryptoAmount: page.receivedCryptoAmount,
          distributedCryptoAmount: page.distributedCryptoAmount,
          reconciliationStatus: page.reconciliationStatus,
          cryptoCurrency: page.cryptoCurrency,
          paidAt: page.paidAt,
          txHash: page.txHash,
          settlementTxHash: page.settlementTxHash,
          disposableAddress: page.disposableAddress,
          merchantReferences: page.merchantReferences,
        });
        setSwapAmount('');
        setError(null);
      } catch (loadError) {
        console.error('Failed to load payment page', loadError);
        if (active) {
          setError('We could not load this payment page.');
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, [uniqueHash]);

  useEffect(() => {
    if (!uniqueHash || !paymentPage || FINAL_STATUSES.has(paymentPage.status)) {
      setFiatOptions([]);
      setFiatOptionsLoading(false);
      return;
    }

    let active = true;

    const loadFiatOptions = async () => {
      setFiatOptionsLoading(true);
      try {
        const resolvedBuyerContext = await buyerLocationService.resolve();
        if (!active) {
          return;
        }

        const optionsResult = await publicPaymentService.getFiatOptions(uniqueHash, resolvedBuyerContext);
        if (!active) {
          return;
        }

        setBuyerContext({
          payerCountryCode: optionsResult.buyerCountryCode ?? resolvedBuyerContext?.payerCountryCode ?? null,
          payerSubdivisionCode: optionsResult.buyerSubdivisionCode ?? resolvedBuyerContext?.payerSubdivisionCode ?? null,
        });
        setFiatOptions(optionsResult.fiatOptions ?? []);
      } catch (optionsError) {
        console.error('Failed to load fiat options', optionsError);
        if (active) {
          setFiatOptions([]);
        }
      } finally {
        if (active) {
          setFiatOptionsLoading(false);
        }
      }
    };

    void loadFiatOptions();

    return () => {
      active = false;
    };
  }, [paymentPage, uniqueHash]);

  const reloadFiatOptionsForCurrentBuyer = useCallback(async () => {
    if (!uniqueHash) {
      return;
    }

    setFiatOptionsLoading(true);
    try {
      const optionsResult = await publicPaymentService.getFiatOptions(uniqueHash, buyerContext);
      setBuyerContext({
        payerCountryCode: optionsResult.buyerCountryCode ?? buyerContext?.payerCountryCode ?? null,
        payerSubdivisionCode: optionsResult.buyerSubdivisionCode ?? buyerContext?.payerSubdivisionCode ?? null,
      });
      setFiatOptions(optionsResult.fiatOptions ?? []);
    } catch (refreshError) {
      console.error('Failed to refresh fiat options after provider launch error', refreshError);
      setFiatOptions([]);
    } finally {
      setFiatOptionsLoading(false);
    }
  }, [buyerContext, uniqueHash]);

  useEffect(() => {
    if (!paymentPage) {
      return;
    }

    if (activeFlow !== 'menu') {
      return;
    }

    const source = status?.paymentSource || paymentPage.paymentSource;
    const nextFlow = resolveFlowFromPaymentSource(source);
    const currentStatus = status?.status || paymentPage.status;

    if (currentStatus !== 'Issued') {
      setActiveFlow(nextFlow);
    }
  }, [activeFlow, paymentPage, status?.paymentSource, status?.status]);

  useEffect(() => {
    if (!uniqueHash || !paymentPage) {
      return;
    }

    const effectiveStatus = status?.status || paymentPage.status;
    if (FINAL_STATUSES.has(effectiveStatus)) {
      return;
    }

    const poll = window.setInterval(async () => {
      try {
        const next = await publicPaymentService.getPaymentStatus(uniqueHash);
        setStatus(next);
      } catch (pollError) {
        console.error('Failed to refresh payment status', pollError);
      }
    }, STATUS_POLL_INTERVAL_MS);

    return () => window.clearInterval(poll);
  }, [paymentPage, status?.status, uniqueHash]);

  useEffect(() => {
    if (!uniqueHash || !swapExchange.publicId || !paymentPage) {
      return;
    }

    const effectiveStatus = status?.status || paymentPage.status;
    if (FINAL_STATUSES.has(effectiveStatus)) {
      return;
    }

    let active = true;
    const poll = window.setInterval(async () => {
      try {
        const response = await publicPaymentService.getSimpleSwapExchange(uniqueHash, swapExchange.publicId || '');
        if (!active) {
          return;
        }

        const result = response?.result as Record<string, unknown> | undefined;
        const providerStatus = readString(result?.status);
        const redirectUrl = readString(result?.redirectUrl) || readString(result?.url);
        const depositAddress = readString(result?.addressFrom) || readString(result?.address_from);
        const depositAmount = parseUnknownString(
          result?.amountFrom ?? result?.amount_from ?? result?.depositAmount ?? result?.fromAmount
        );
        const depositTicker = normalizeKey(
          parseUnknownString(result?.tickerFrom ?? result?.ticker_from ?? result?.coinFrom ?? result?.currency_from)
        );
        const depositNetwork = normalizeSimpleSwapNetwork(
          parseUnknownString(result?.networkFrom ?? result?.network_from ?? result?.chainFrom ?? result?.chain_from)
        );

        setSwapExchange(current => ({
          publicId: current.publicId,
          status: providerStatus || current.status,
          redirectUrl: redirectUrl || current.redirectUrl,
          depositAddress: depositAddress || current.depositAddress,
          depositAmount: depositAmount || current.depositAmount,
          depositTicker: depositTicker || current.depositTicker,
          depositNetwork: depositNetwork || current.depositNetwork,
        }));
      } catch (pollError) {
        console.error('Failed to refresh RizzSwap exchange', pollError);
      }
    }, 15000);

    return () => {
      active = false;
      window.clearInterval(poll);
    };
  }, [paymentPage, status?.status, swapExchange.publicId, uniqueHash]);

  useEffect(() => {
    if (!paymentPage?.uniqueWalletAddress) {
      setQrCodeDataUrl(null);
      return;
    }

    const qrPayload = buildDirectUsdcQrPayload(paymentPage);
    if (!qrPayload) {
      setQrCodeDataUrl(null);
      return;
    }

    let active = true;
    void QRCode.toDataURL(qrPayload, {
      width: 280,
      margin: 1,
      color: { dark: '#111827', light: '#FFFFFF' },
    })
      .then(url => {
        if (active) {
          setQrCodeDataUrl(url);
        }
      })
      .catch(qrError => {
        console.error('Failed to generate QR code', qrError);
        if (active) {
          setQrCodeDataUrl(null);
        }
      });

    return () => {
      active = false;
    };
  }, [
    paymentPage,
    paymentPage?.cryptoAmount,
    paymentPage?.cryptoCurrency,
    paymentPage?.cryptoNetwork,
    paymentPage?.quotedCryptoAmount,
    paymentPage?.uniqueWalletAddress,
  ]);

  useEffect(() => {
    if (!swapExchange.depositAddress) {
      setSwapDepositQrCodeDataUrl(null);
      return;
    }

    const qrPayload = buildSwapDepositQrPayload(
      swapExchange.depositAddress,
      swapExchange.depositAmount || swapAmount,
      swapExchange.depositTicker || swapFromTicker,
      swapExchange.depositNetwork || swapFromNetwork
    );
    if (!qrPayload) {
      setSwapDepositQrCodeDataUrl(null);
      return;
    }

    let active = true;
    void QRCode.toDataURL(qrPayload, {
      width: 220,
      margin: 1,
      color: { dark: '#111827', light: '#FFFFFF' },
    })
      .then(url => {
        if (active) {
          setSwapDepositQrCodeDataUrl(url);
        }
      })
      .catch(qrError => {
        console.error('Failed to generate RizzSwap deposit QR code', qrError);
        if (active) {
          setSwapDepositQrCodeDataUrl(null);
        }
      });

    return () => {
      active = false;
    };
  }, [
    swapAmount,
    swapExchange.depositAddress,
    swapExchange.depositAmount,
    swapExchange.depositNetwork,
    swapExchange.depositTicker,
    swapFromNetwork,
    swapFromTicker,
  ]);

  useEffect(() => {
    if (!swapTickerPickerOpen && !swapNetworkPickerOpen) {
      return;
    }

    const closePickers = () => {
      setSwapTickerPickerOpen(false);
      setSwapNetworkPickerOpen(false);
    };

    window.addEventListener('click', closePickers);
    return () => window.removeEventListener('click', closePickers);
  }, [swapNetworkPickerOpen, swapTickerPickerOpen]);

  useEffect(() => {
    if (!paymentPage?.quoteExpiresAt) {
      return;
    }

    const effectiveStatus = status?.status || paymentPage.status;
    if (FINAL_STATUSES.has(effectiveStatus)) {
      return;
    }

    const timer = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [paymentPage?.quoteExpiresAt, paymentPage?.status, status?.status]);

  const providerOptions = chooseProviderOptions(fiatOptions);
  const selectedProvider = providerOptions.find(option => option.sessionId === selectedFiatSessionId)
    || providerOptions[0]
    || null;
  const fiatMethodUnavailable = !fiatOptionsLoading && providerOptions.length === 0;
  const fiatMethodDisabled = fiatOptionsLoading || fiatMethodUnavailable || fiatLaunchInProgress || fiatProviderIssueSubmitting;
  const fiatMethodDescription = fiatOptionsLoading
    ? 'Checking availability for this payment...'
    : fiatMethodUnavailable
      ? 'Buy USDC with Credit card, Apple/Google pay, Bank transfer and over 160 Payment methods'
      : 'Buy USDC with Credit card, Apple/Google pay, Bank transfer and over 160 Payment methods';

  useEffect(() => {
    if (providerOptions.length === 0) {
      setSelectedFiatSessionId(null);
      setLastAttemptedFiatProvider(null);
      return;
    }

    if (!selectedFiatSessionId || !providerOptions.some(option => option.sessionId === selectedFiatSessionId)) {
      setSelectedFiatSessionId(providerOptions[0].sessionId);
    }
  }, [providerOptions, selectedFiatSessionId]);

  const effectiveStatus = status?.status || paymentPage?.status || 'Issued';
  const effectiveDisplayStatus = status?.displayStatus || paymentPage?.displayStatus || effectiveStatus;
  const isFinal = FINAL_STATUSES.has(effectiveStatus);
  const isUnderpaidFinal = isFinal && isUnderpaidDisplayStatus(effectiveDisplayStatus);
  const isSuccessFinal = isSuccessFinalStatus(effectiveStatus, effectiveDisplayStatus);
  const receivedCryptoAmount = status?.receivedCryptoAmount ?? paymentPage?.receivedCryptoAmount;
  const receivedCryptoCurrency = status?.cryptoCurrency || paymentPage?.cryptoCurrency || '';
  const receivedAmountLabel = (
    typeof receivedCryptoAmount === 'number'
    && Number.isFinite(receivedCryptoAmount)
    && receivedCryptoAmount > 0
    && receivedCryptoCurrency
  )
    ? `${receivedCryptoCurrency} ${formatAmount(receivedCryptoAmount, receivedCryptoCurrency)}`
    : null;
  const requiredCryptoAmount = paymentPage?.quotedCryptoAmount;
  const requiredCryptoCurrency = paymentPage?.cryptoCurrency || receivedCryptoCurrency;
  const requiredCryptoAmountLabel = (
    typeof requiredCryptoAmount === 'number'
    && Number.isFinite(requiredCryptoAmount)
    && requiredCryptoAmount > 0
    && requiredCryptoCurrency
  )
    ? `${requiredCryptoCurrency} ${formatAmount(requiredCryptoAmount, requiredCryptoCurrency)}`
    : null;
  const underpaidMissingCryptoAmount = (
    typeof requiredCryptoAmount === 'number'
    && Number.isFinite(requiredCryptoAmount)
    && requiredCryptoAmount > 0
    && typeof receivedCryptoAmount === 'number'
    && Number.isFinite(receivedCryptoAmount)
    && receivedCryptoAmount > 0
  )
    ? Math.max(requiredCryptoAmount - receivedCryptoAmount, 0)
    : null;
  const underpaidMissingAmountLabel = (
    typeof underpaidMissingCryptoAmount === 'number'
    && Number.isFinite(underpaidMissingCryptoAmount)
    && underpaidMissingCryptoAmount > 0
    && requiredCryptoCurrency
  )
    ? `${requiredCryptoCurrency} ${formatAmount(underpaidMissingCryptoAmount, requiredCryptoCurrency)}`
    : null;
  const topUpPaymentSource = status?.paymentSource || paymentPage?.paymentSource || '';
  const normalizedTopUpPaymentSource = normalizeKey(topUpPaymentSource);
  const isDirectTopUpEligible = normalizedTopUpPaymentSource.includes('directcrypto');
  const isCryptoSwapPayment = normalizedTopUpPaymentSource.includes('cryptoswap');
  const observedSettlementHash =
    status?.settlementTxHash
    || paymentPage?.settlementTxHash
    || status?.txHash
    || paymentPage?.txHash;
  const minimumFundingAmount = (
    typeof requiredCryptoAmount === 'number'
    && Number.isFinite(requiredCryptoAmount)
    && requiredCryptoAmount > 0
  )
    ? requiredCryptoAmount * MINIMUM_ON_CHAIN_COMPLETION_RATIO
    : null;
  const underpaidTopUpRequired = Boolean(
    !isFinal
    && isDirectTopUpEligible
    && !observedSettlementHash
    && typeof minimumFundingAmount === 'number'
    && typeof receivedCryptoAmount === 'number'
    && Number.isFinite(receivedCryptoAmount)
    && receivedCryptoAmount > 0
    && receivedCryptoAmount + CRYPTO_AMOUNT_EPSILON < minimumFundingAmount
    && typeof underpaidMissingCryptoAmount === 'number'
    && underpaidMissingCryptoAmount > CRYPTO_AMOUNT_EPSILON
  );
  const swapUnderpaymentReviewRequired = Boolean(
    !isFinal
    && isCryptoSwapPayment
    && effectiveDisplayStatus === SWAP_UNDERPAYMENT_REVIEW_STATUS
    && typeof receivedCryptoAmount === 'number'
    && Number.isFinite(receivedCryptoAmount)
    && receivedCryptoAmount > 0
    && typeof underpaidMissingCryptoAmount === 'number'
    && underpaidMissingCryptoAmount > CRYPTO_AMOUNT_EPSILON
  );
  const showMerchantReturnButton = shouldShowMerchantReturnButton(
    effectiveStatus,
    effectiveDisplayStatus,
    paymentPage?.hasTrustedMerchantReturnUrl
  );
  const flowFromSource = resolveFlowFromPaymentSource(status?.paymentSource || paymentPage?.paymentSource);
  const detailsFlow = activeFlow === 'menu' ? flowFromSource : activeFlow;
  const submittedFromStatus = isSubmittedStatus(effectiveStatus, effectiveDisplayStatus)
    && hasSubmissionEvidence(status, paymentPage);
  const swapSubmittedFromProvider = isSimpleSwapSubmittedStatus(swapExchange.status);
  const showSubmittedPage = !isFinal && !underpaidTopUpRequired && !swapUnderpaymentReviewRequired && (
    detailsFlow === 'swap'
      ? Boolean(swapExchange.publicId) && (swapSubmittedFromProvider || submittedFromStatus)
      : submittedFromStatus
  );
  const submittedPaymentProgressSteps = detailsFlow === 'swap'
    ? buildSwapProgressSteps(swapExchange.status, isSuccessFinal)
    : buildDirectProgressSteps(effectiveStatus, effectiveDisplayStatus, submittedFromStatus, isSuccessFinal);
  const directActiveProgressSteps = buildDirectProgressSteps(
    effectiveStatus,
    effectiveDisplayStatus,
    submittedFromStatus,
    isSuccessFinal
  );
  const swapActiveProgressSteps = buildSwapProgressSteps(swapExchange.status, isSuccessFinal);
  const swapDepositProgressSteps = buildSwapProgressSteps(swapExchange.status, false);

  const paymentReference = paymentPage?.invoice?.invoiceNumber?.trim()
    || paymentPage?.id?.slice(0, 8).toUpperCase()
    || 'Pending';
  const invoiceDetails = paymentPage?.invoice || null;
  const invoiceItems = (invoiceDetails?.items || []).map(item => {
    const quantity = Number(item.quantity || 0);
    const unitPrice = Number(item.unitPrice || 0);
    const amount = Number(item.amount ?? quantity * unitPrice);
    return {
      itemName: (item.itemName || '').trim() || 'Invoice item',
      quantity: Number.isFinite(quantity) ? quantity : 0,
      unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
      amount: Number.isFinite(amount) ? amount : 0,
    };
  });
  const invoiceSubtotal = typeof invoiceDetails?.subtotal === 'number'
    ? invoiceDetails.subtotal
    : invoiceItems.reduce((sum, item) => sum + item.amount, 0);
  const invoiceTaxAmount = typeof invoiceDetails?.taxAmount === 'number'
    ? invoiceDetails.taxAmount
    : 0;
  const invoiceTotalAmount = typeof invoiceDetails?.total === 'number' && invoiceDetails.total > 0
    ? invoiceDetails.total
    : (paymentPage?.fiatAmount || 0);
  const paymentAmountLabel = paymentPage
    ? `${paymentPage.fiatCurrency} ${formatAmount(paymentPage.fiatAmount, paymentPage.fiatCurrency)}`
    : '';
  const invoiceAmountLabel = paymentPage
    ? `${paymentPage.fiatCurrency} ${formatAmount(invoiceTotalAmount, paymentPage.fiatCurrency)}`
    : '';
  const invoiceIssuerCompanyName = invoiceDetails?.issuerCompanyName?.trim() || 'Not provided';
  const invoicePayingCompanyName = invoiceDetails?.payingCompanyName?.trim() || 'Not provided';
  const publicMerchantName = invoiceDetails?.issuerCompanyName?.trim() || 'Business';
  const paymentAmountHeroLabel = paymentPage
    ? `${formatAmount(invoiceTotalAmount, paymentPage.fiatCurrency)} ${paymentPage.fiatCurrency}`
    : '';
  const directCryptoSymbol = paymentPage?.cryptoCurrency || 'USDC';
  const directNetworkName = paymentPage?.cryptoNetwork || 'Polygon';
  const directCryptoIconUrl = `https://static.simpleswap.io/images/currencies-logo/${normalizeKey(directCryptoSymbol) || 'usdc'}.svg`;
  const directNetworkIconUrl = resolveSimpleSwapNetworkIconUrl(directNetworkName);
  const selectedMethodLabel = detailsFlow === 'fiat'
    ? 'Buy USDC'
    : detailsFlow === 'crypto'
      ? 'Pay with crypto'
      : detailsFlow === 'swap'
      ? 'Swap crypto'
      : 'USDC (POL)';
  const selectedProviderLabel = detailsFlow === 'fiat'
    ? (selectedProvider?.provider || status?.provider || paymentPage?.provider || 'Onramper')
    : detailsFlow === 'swap'
      ? 'RizzSwap'
      : 'Wallet transfer';
  const quoteCountdown = (() => {
    if (!paymentPage?.quoteExpiresAt) {
      return null;
    }

    const diffMs = new Date(paymentPage.quoteExpiresAt).getTime() - clock;
    if (diffMs <= 0) {
      return 'Updating amount';
    }

    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')} remaining`;
  })();

  const swapOption = paymentPage?.cryptoSwapOption || null;
  const swapProviderKind = normalizeKey(swapOption?.providerKind || swapOption?.provider);
  const swapDestinationTicker = normalizeKey(swapOption?.toTokenSymbol);
  const swapSupportsSimpleSwap = swapProviderKind.includes('simpleswap');
  const swapReady = Boolean(
    swapOption?.isEnabled &&
    swapSupportsSimpleSwap &&
    swapOption.destinationAddress &&
    swapOption.toTokenSymbol &&
    swapOption.toChain
  );
  const swapTickerOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { symbol: string; label: string; imageUrl: string | null }[] = [];

    swapCurrencies.forEach(currency => {
      if (seen.has(currency.symbol)) {
        return;
      }

      seen.add(currency.symbol);
      options.push({
        symbol: currency.symbol,
        label: `${currency.symbol.toUpperCase()}${currency.displayName ? ` - ${currency.displayName}` : ''}`,
        imageUrl: currency.imageUrl,
      });
    });

    return options;
  }, [swapCurrencies]);

  const swapNetworkOptions = useMemo(() => {
    const seen = new Set<string>();
    return swapCurrencies
      .filter(currency => currency.symbol === swapFromTicker)
      .filter(currency => {
        if (seen.has(currency.network)) {
          return false;
        }

        seen.add(currency.network);
        return true;
      })
      .map(currency => ({
        network: currency.network,
        label: currency.network.toUpperCase(),
        iconUrl: resolveSimpleSwapNetworkIconUrl(currency.network),
      }));
  }, [swapCurrencies, swapFromTicker]);
  const filteredSwapTickerOptions = useMemo(() => {
    const query = swapTickerSearch.trim().toLowerCase();
    if (!query) {
      return swapTickerOptions.slice(0, 140);
    }

    return swapTickerOptions
      .filter(option => option.label.toLowerCase().includes(query) || option.symbol.includes(query))
      .slice(0, 140);
  }, [swapTickerOptions, swapTickerSearch]);
  const swapAssetNetworkOptions = useMemo<SwapAssetNetworkOption[]>(() => {
    const seen = new Set<string>();
    return swapCurrencies
      .filter(currency => {
        if (seen.has(currency.key)) {
          return false;
        }

        seen.add(currency.key);
        return true;
      })
      .map(currency => ({
        symbol: currency.symbol,
        network: currency.network,
        label: `${currency.symbol.toUpperCase()}${currency.displayName ? ` - ${currency.displayName}` : ''}`,
        displayName: currency.displayName,
        networkLabel: currency.network.toUpperCase(),
        imageUrl: currency.imageUrl,
        iconUrl: resolveSimpleSwapNetworkIconUrl(currency.network),
        key: currency.key,
      }))
      .sort(compareSwapAssetNetworkOptions);
  }, [swapCurrencies]);
  const swapFeaturedRouteOptions = useMemo(() => {
    const selected = new Set<string>();
    const options: SwapAssetNetworkOption[] = [];

    popularSwapSymbols.forEach(symbol => {
      const preferredNetworks = preferredSwapNetworksBySymbol[symbol] || [];
      const preferred = preferredNetworks
        .map(network => swapAssetNetworkOptions.find(option =>
          option.symbol === symbol &&
          normalizeSimpleSwapNetwork(option.network) === network
        ))
        .find(Boolean);
      const fallback = swapAssetNetworkOptions.find(option => option.symbol === symbol);
      const option = preferred || fallback;

      if (option && !selected.has(option.key)) {
        selected.add(option.key);
        options.push(option);
      }
    });

    return options.slice(0, 8);
  }, [swapAssetNetworkOptions]);
  const swapRouteNetworkFilters = useMemo(() => {
    const counts = new Map<string, { key: string; label: string; iconUrl: string | null; count: number }>();

    swapAssetNetworkOptions.forEach(option => {
      const key = normalizeSimpleSwapNetwork(option.network) || option.network;
      const existing = counts.get(key);
      if (existing) {
        existing.count += 1;
        return;
      }

      counts.set(key, {
        key,
        label: option.networkLabel,
        iconUrl: option.iconUrl,
        count: 1,
      });
    });

    return Array.from(counts.values())
      .sort((left, right) =>
        getPopularNetworkRank(left.key) - getPopularNetworkRank(right.key)
        || left.label.localeCompare(right.label)
      );
  }, [swapAssetNetworkOptions]);
  const filteredSwapAssetNetworkOptions = useMemo(() => {
    const query = swapTickerSearch.trim().toLowerCase();
    const options = swapAssetNetworkOptions.filter(option =>
      swapRouteNetworkFilter === 'all' ||
      normalizeSimpleSwapNetwork(option.network) === swapRouteNetworkFilter
    );
    if (!query) {
      return options.slice(0, 160);
    }

    return options
      .filter(option =>
        option.label.toLowerCase().includes(query) ||
        option.displayName.toLowerCase().includes(query) ||
        option.symbol.includes(query) ||
        option.network.includes(query) ||
        option.networkLabel.toLowerCase().includes(query)
      )
      .slice(0, 160);
  }, [swapAssetNetworkOptions, swapRouteNetworkFilter, swapTickerSearch]);
  const selectedSwapTickerOption = useMemo(
    () => swapTickerOptions.find(option => option.symbol === swapFromTicker) || null,
    [swapFromTicker, swapTickerOptions]
  );
  const selectedSwapNetworkOption = useMemo(
    () => swapNetworkOptions.find(option => option.network === swapFromNetwork) || null,
    [swapFromNetwork, swapNetworkOptions]
  );
  const selectedSwapAssetNetworkOption = useMemo(
    () => swapAssetNetworkOptions.find(option => option.symbol === swapFromTicker && option.network === swapFromNetwork) || null,
    [swapAssetNetworkOptions, swapFromNetwork, swapFromTicker]
  );
  const selectSwapAssetNetworkOption = useCallback((option: SwapAssetNetworkOption) => {
    setSwapFromTicker(option.symbol);
    setSwapFromNetwork(option.network);
    setSwapAmount('');
    setSwapRateId(null);
    setSwapExchange(createEmptySwapExchange());
    setSwapDepositQrCodeDataUrl(null);
    setCopiedSwapDepositAddress(false);
    setSwapTickerPickerOpen(false);
    setSwapNetworkPickerOpen(false);
  }, []);
  useEffect(() => {
    if (
      swapRouteNetworkFilter !== 'all' &&
      !swapRouteNetworkFilters.some(option => option.key === swapRouteNetworkFilter)
    ) {
      setSwapRouteNetworkFilter('all');
    }
  }, [swapRouteNetworkFilter, swapRouteNetworkFilters]);
  const swapDestinationNetwork = useMemo(() => {
    const preferred = normalizeSimpleSwapNetwork(swapOption?.toChain);
    if (!swapDestinationTicker) {
      return preferred;
    }

    const destinationNetworks = swapCurrencies
      .filter(currency => currency.symbol === swapDestinationTicker)
      .map(currency => currency.network);

    if (destinationNetworks.length === 0) {
      return preferred;
    }

    if (preferred && destinationNetworks.includes(preferred)) {
      return preferred;
    }

    return destinationNetworks[0];
  }, [swapCurrencies, swapDestinationTicker, swapOption?.toChain]);

  useEffect(() => {
    if (!uniqueHash || (activeFlow !== 'swap' && activeFlow !== 'crypto') || !swapReady) {
      return;
    }

    let active = true;
    const loadSimpleSwapCurrencies = async () => {
      setSwapCurrenciesLoading(true);
      try {
        const envelope = await publicPaymentService.getSimpleSwapCurrencies(uniqueHash);
        if (!active) {
          return;
        }

        const result = Array.isArray(envelope?.result)
          ? (envelope.result as SimpleSwapCurrencyResult[])
          : [];
        const normalized = result
          .map(item => {
            const symbol = normalizeKey(readString(item.ticker) || readString(item.symbol));
            const network = normalizeSimpleSwapNetwork(readString(item.network) || readString(item.chain));
            if (!symbol || !network) {
              return null;
            }

            const isEnabled = item.enabled !== false;
            const supportsFloat = item.isAvailableFloat !== false;
            if (!isEnabled || !supportsFloat) {
              return null;
            }

            return {
              symbol,
              network,
              displayName: readString(item.name) || symbol.toUpperCase(),
              key: `${symbol}:${network}`,
              imageUrl: readString(item.image),
            } satisfies SimpleSwapCurrencyOption;
          })
          .filter((item): item is SimpleSwapCurrencyOption => item !== null);

        setSwapCurrencies(normalized);
      } catch (currenciesError) {
        console.error('Failed to load RizzSwap currencies', currenciesError);
        if (active) {
          setSwapCurrencies([]);
          setSwapError(extractApiErrorMessage(currenciesError, 'Could not load available RizzSwap assets.'));
        }
      } finally {
        if (active) {
          setSwapCurrenciesLoading(false);
        }
      }
    };

    void loadSimpleSwapCurrencies();
    return () => {
      active = false;
    };
  }, [activeFlow, swapReady, uniqueHash]);

  useEffect(() => {
    if (swapTickerOptions.length === 0) {
      setSwapFromTicker('');
      setSwapFromNetwork('');
      setSwapTickerSearch('');
      setSwapRateId(null);
      return;
    }

    if (!swapFromTicker || !swapTickerOptions.some(option => option.symbol === swapFromTicker)) {
      const preferredTicker = swapTickerOptions.some(option => option.symbol === 'btc')
        ? 'btc'
        : swapTickerOptions[0].symbol;
      setSwapFromTicker(preferredTicker);
    }
  }, [swapFromTicker, swapTickerOptions]);

  useEffect(() => {
    if (swapNetworkOptions.length === 0) {
      setSwapFromNetwork('');
      setSwapRateId(null);
      return;
    }

    if (!swapFromNetwork || !swapNetworkOptions.some(option => option.network === swapFromNetwork)) {
      const preferredNetwork = swapNetworkOptions.some(option => option.network === 'btc')
        ? 'btc'
        : swapNetworkOptions[0].network;
      setSwapFromNetwork(preferredNetwork);
    }
  }, [swapFromNetwork, swapNetworkOptions]);

  useEffect(() => {
    setSwapNetworkPickerOpen(false);
  }, [swapFromTicker]);

  useEffect(() => {
    if (!uniqueHash || !paymentPage || !swapOption || (activeFlow !== 'swap' && activeFlow !== 'crypto') || !swapReady) {
      return;
    }

    if (!swapFromTicker || !swapFromNetwork || !swapDestinationTicker || !swapDestinationNetwork || !paymentPage.quotedCryptoAmount) {
      return;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      setSwapEstimating(true);
      try {
        const estimateEnvelope = await publicPaymentService.getSimpleSwapEstimate(uniqueHash, {
          tickerFrom: swapFromTicker,
          networkFrom: swapFromNetwork,
          tickerTo: swapDestinationTicker,
          networkTo: swapDestinationNetwork,
          amount: `${paymentPage.quotedCryptoAmount}`,
          fixed: true,
          reverse: true,
        });

        if (!active) {
          return;
        }

        const estimateResult = estimateEnvelope?.result as Record<string, unknown> | undefined;
        const sourceAmount = parseSourceAmountFromEstimate(estimateResult);
        if (!sourceAmount) {
          throw new Error('RizzSwap did not return an estimated source amount.');
        }
        const estimateRateId = readString(estimateResult?.rateId);

        setSwapAmount(sourceAmount);
        setSwapRateId(estimateRateId);
        setSwapError(null);
      } catch (estimateError) {
        console.error('Failed to estimate RizzSwap source amount', estimateError);
        if (active) {
          setSwapAmount('');
          setSwapRateId(null);
          setSwapError(extractApiErrorMessage(estimateError, 'Could not calculate the exact amount to send from RizzSwap.'));
        }
      } finally {
        if (active) {
          setSwapEstimating(false);
        }
      }
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [
    activeFlow,
    paymentPage,
    swapFromNetwork,
    swapFromTicker,
    swapDestinationNetwork,
    swapDestinationTicker,
    swapOption,
    swapReady,
    uniqueHash,
  ]);

  const refreshStatus = async () => {
    if (!uniqueHash) {
      return;
    }

    try {
      const next = await publicPaymentService.getPaymentStatus(uniqueHash);
      setStatus(next);
    } catch (refreshError) {
      console.error('Failed to refresh payment status', refreshError);
    }
  };

  const startCryptoFlow = () => {
    if (!uniqueHash) {
      return;
    }

    setActiveFlow('crypto');
    setStatusMessage(null);
    setFiatError(null);
    setSwapError(null);
    setSwapTickerPickerOpen(false);
    setSwapNetworkPickerOpen(false);
  };

  const startFiatFlow = async () => {
    if (!uniqueHash) {
      return;
    }
    if (fiatMethodDisabled) {
      return;
    }

    const providerToLaunch = selectedProvider || providerOptions[0] || null;
    if (!providerToLaunch) {
      setFiatError('Not yet available in your country');
      return;
    }

    setStatusMessage(`Opening secure checkout with ${providerToLaunch.provider}. This page will update automatically.`);
    setFiatError(null);
    setSwapError(null);

    await handleContinueWithProvider(providerToLaunch);
  };

  const goToMethodMenu = () => {
    setActiveFlow('menu');
    setFiatError(null);
    setSwapError(null);
    setStatusMessage(null);
    setSwapRateId(null);
    setSwapTickerPickerOpen(false);
    setSwapNetworkPickerOpen(false);
  };

  const goToCryptoOptions = () => {
    setActiveFlow('crypto');
    setFiatError(null);
    setSwapError(null);
    setStatusMessage('Send USDC directly or choose another crypto to swap into USDC.');
    setSwapRateId(null);
    setSwapTickerPickerOpen(false);
    setSwapNetworkPickerOpen(false);
  };

  const handleCopyWallet = async () => {
    if (!paymentPage?.uniqueWalletAddress) {
      return;
    }

    try {
      await navigator.clipboard.writeText(paymentPage.uniqueWalletAddress);
      setCopiedWallet(true);
      window.setTimeout(() => setCopiedWallet(false), 2000);
    } catch (clipboardError) {
      console.error('Failed to copy wallet', clipboardError);
    }
  };

  const handleCopyTopUpAmount = async () => {
    if (
      typeof underpaidMissingCryptoAmount !== 'number'
      || !Number.isFinite(underpaidMissingCryptoAmount)
      || underpaidMissingCryptoAmount <= 0
      || !requiredCryptoCurrency
    ) {
      return;
    }

    try {
      await navigator.clipboard.writeText(formatClipboardAmount(underpaidMissingCryptoAmount, requiredCryptoCurrency));
      setCopiedTopUpAmount(true);
      window.setTimeout(() => setCopiedTopUpAmount(false), 2000);
    } catch (clipboardError) {
      console.error('Failed to copy remaining payment amount', clipboardError);
    }
  };

  const openExplorer = () => {
    if (!paymentPage?.uniqueWalletAddress) {
      return;
    }

    window.open(`https://polygonscan.com/address/${paymentPage.uniqueWalletAddress}`, '_blank', 'noopener,noreferrer');
  };

  const buildInvoiceDocumentHtml = (includeDownloadHint: boolean): string => {
    const page = paymentPage;
    if (!page) {
      return '';
    }
    const brandLogoUrl = typeof window !== 'undefined'
      ? `${window.location.origin}${RIZZPAY_LOGO_PUBLIC_PATH}`
      : RIZZPAY_LOGO_PUBLIC_PATH;

    const rowsMarkup = invoiceItems.length > 0
      ? invoiceItems
        .map(item => `
          <tr>
            <td>${escapeHtml(item.itemName)}</td>
            <td>${escapeHtml(`${item.quantity}`)}</td>
            <td>${escapeHtml(`${formatAmount(item.unitPrice, page.fiatCurrency)} ${page.fiatCurrency}`)}</td>
            <td>${escapeHtml(`${formatAmount(item.amount, page.fiatCurrency)} ${page.fiatCurrency}`)}</td>
          </tr>
        `)
        .join('')
      : '<tr><td colspan="4">No line items provided.</td></tr>';

    const actionHint = includeDownloadHint
      ? 'Tip: choose "Save as PDF" in the print dialog to download your invoice.'
      : '';

    return `
<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Invoice ${escapeHtml(paymentReference)}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; background: #f7f9fc; color: #0f172a; }
    .sheet { max-width: 860px; margin: 0 auto; background: #ffffff; min-height: 100vh; padding: 28px; }
    .brand { display: inline-flex; align-items: center; gap: 10px; margin-bottom: 14px; }
    .brand img { width: 36px; height: 36px; object-fit: contain; }
    .brand-copy { display: grid; gap: 2px; line-height: 1.1; }
    .brand-name { font-size: 18px; font-weight: 700; color: #0f172a; }
    .brand-tagline { font-size: 11px; color: #64748b; }
    .top { display: flex; justify-content: space-between; gap: 18px; align-items: flex-start; }
    .title { margin: 0; font-size: 30px; line-height: 1.1; }
    .meta { margin-top: 8px; color: #475569; font-size: 14px; }
    .status { border: 1px solid #dbe7fb; background: #eff6ff; border-radius: 999px; padding: 6px 12px; color: #1d4ed8; font-weight: 600; font-size: 12px; }
    .hint { margin-top: 12px; border: 1px solid #dbeafe; background: #f0f8ff; color: #1e3a8a; border-radius: 10px; padding: 10px 12px; font-size: 13px; }
    .grid { margin-top: 20px; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
    .box { border: 1px solid #dbe3ef; border-radius: 12px; padding: 12px; }
    .box h4 { margin: 0 0 8px; font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; color: #64748b; }
    .box strong { display: block; font-size: 16px; margin-top: 2px; }
    table { width: 100%; border-collapse: collapse; margin-top: 18px; border: 1px solid #dbe3ef; border-radius: 12px; overflow: hidden; }
    th, td { padding: 10px 12px; border-bottom: 1px solid #e2e8f0; text-align: left; font-size: 14px; }
    th { background: #f8fafc; color: #334155; font-weight: 600; }
    tr:last-child td { border-bottom: 0; }
    .totals { margin-top: 16px; margin-left: auto; width: min(360px, 100%); border: 1px solid #dbe3ef; border-radius: 12px; padding: 12px; }
    .totals-row { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 8px; color: #334155; }
    .totals-row:last-child { margin-bottom: 0; font-weight: 700; color: #0f172a; font-size: 16px; }
    .wallet { margin-top: 18px; border: 1px solid #dbe3ef; border-radius: 12px; padding: 12px; }
    .wallet-header { display: flex; justify-content: space-between; gap: 14px; align-items: flex-start; }
    .wallet-copy { font-size: 14px; color: #475569; margin-top: 6px; }
    .wallet-address { margin-top: 10px; padding: 10px; border: 1px solid #d7dee9; border-radius: 10px; background: #f8fbff; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: 12px; word-break: break-all; }
    .qr img { width: 160px; height: 160px; border: 1px solid #d7dee9; border-radius: 10px; padding: 6px; background: #fff; }
    .notes { margin-top: 16px; border: 1px solid #dbe7fb; border-radius: 12px; background: #f8fbff; padding: 12px; color: #334155; }
    @media print {
      body { background: #fff; }
      .sheet { max-width: none; min-height: auto; padding: 0; }
      .hint { display: none; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <div>
        <div class="brand">
          <img src="${escapeHtml(brandLogoUrl)}" alt="RizzPay logo" />
          <div class="brand-copy">
            <div class="brand-name">RizzPay</div>
            <div class="brand-tagline">${escapeHtml(RIZZPAY_TAGLINE)}</div>
          </div>
        </div>
        <h1 class="title">Invoice</h1>
        <div class="meta">Order # ${escapeHtml(paymentReference)}</div>
      </div>
      <div class="status">Waiting for payment</div>
    </div>
    ${actionHint ? `<div class="hint">${escapeHtml(actionHint)}</div>` : ''}

    <div class="grid">
      <div class="box">
        <h4>Invoice number</h4>
        <strong>${escapeHtml(paymentReference)}</strong>
      </div>
      <div class="box">
        <h4>Invoice amount</h4>
        <strong>${escapeHtml(invoiceAmountLabel)}</strong>
      </div>
      <div class="box">
        <h4>Issued by</h4>
        <strong>${escapeHtml(invoiceIssuerCompanyName)}</strong>
      </div>
      <div class="box">
        <h4>Issued to</h4>
        <strong>${escapeHtml(invoicePayingCompanyName)}</strong>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>Item</th>
          <th>Qty</th>
          <th>Unit price</th>
          <th>Amount</th>
        </tr>
      </thead>
      <tbody>
        ${rowsMarkup}
      </tbody>
    </table>

    <div class="totals">
      <div class="totals-row"><span>Subtotal</span><span>${escapeHtml(`${formatAmount(invoiceSubtotal, page.fiatCurrency)} ${page.fiatCurrency}`)}</span></div>
      <div class="totals-row"><span>Tax</span><span>${escapeHtml(`${formatAmount(invoiceTaxAmount, page.fiatCurrency)} ${page.fiatCurrency}`)}</span></div>
      <div class="totals-row"><span>Total</span><span>${escapeHtml(invoiceAmountLabel)}</span></div>
    </div>

    <div class="wallet">
      <div class="wallet-header">
        <div>
          <h4 style="margin: 0; font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; color: #64748b;">Destination wallet</h4>
          <div class="wallet-copy">Send ${escapeHtml(page.cryptoCurrency)} on ${escapeHtml(page.cryptoNetwork)} to this one-time wallet.</div>
          <div class="wallet-address">${escapeHtml(page.uniqueWalletAddress)}</div>
        </div>
        <div class="qr">
          ${qrCodeDataUrl ? `<img src="${qrCodeDataUrl}" alt="Payment QR code" />` : ''}
        </div>
      </div>
    </div>

    ${invoiceDetails?.notes?.trim() ? `<div class="notes"><strong>Notes:</strong> ${escapeHtml(invoiceDetails.notes.trim())}</div>` : ''}
  </div>
</body>
</html>
    `.trim();
  };

  const getInvoiceFileBaseName = (): string => {
    const raw = (invoiceDetails?.invoiceNumber?.trim() || paymentReference || 'invoice')
      .replace(/[^a-z0-9-_]+/gi, '-')
      .replace(/^-+|-+$/g, '');
    return raw || 'invoice';
  };

  const openInvoicePrintWindow = (html: string): boolean => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      return false;
    }

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();

    let printed = false;
    const triggerPrint = () => {
      if (printed) {
        return;
      }
      printed = true;

      try {
        printWindow.focus();
        printWindow.print();
      } catch (printError) {
        console.error('Failed to print invoice window', printError);
      }
    };

    if (printWindow.document.readyState === 'complete') {
      triggerPrint();
    } else {
      printWindow.onload = triggerPrint;
      window.setTimeout(triggerPrint, 500);
    }

    return true;
  };

  const printInvoiceDocument = (includeDownloadHint: boolean) => {
    if (!paymentPage?.isInvoice) {
      return;
    }

    const html = buildInvoiceDocumentHtml(includeDownloadHint);
    if (!html) {
      return;
    }

    if (openInvoicePrintWindow(html)) {
      return;
    }

    // Fallback when popup creation is blocked.
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.position = 'fixed';
    frame.style.right = '-10000px';
    frame.style.bottom = '-10000px';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    document.body.appendChild(frame);

    const cleanup = () => {
      window.setTimeout(() => {
        if (frame.parentNode) {
          frame.parentNode.removeChild(frame);
        }
      }, 1200);
    };

    const frameWindow = frame.contentWindow;
    if (!frameWindow) {
      cleanup();
      return;
    }

    const frameDocument = frameWindow.document;
    frameDocument.open();
    frameDocument.write(html);
    frameDocument.close();

    let printed = false;
    const triggerPrint = () => {
      if (printed) {
        return;
      }
      printed = true;

      try {
        frameWindow.focus();
        frameWindow.print();
      } finally {
        cleanup();
      }
    };

    frame.onload = triggerPrint;
    triggerPrint();
  };

  const handlePreviewInvoice = () => setShowInvoicePreview(true);
  const handleDownloadInvoice = () => {
    if (!paymentPage?.isInvoice) {
      return;
    }

    const html = buildInvoiceDocumentHtml(true);
    if (!html) {
      return;
    }

    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${getInvoiceFileBaseName()}.html`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const handlePrintInvoice = () => printInvoiceDocument(false);

  const trustedMerchantReturnActionUrl = useMemo(() => {
    if (!uniqueHash) {
      return '';
    }

    return publicPaymentService.getTrustedMerchantReturnActionUrl(uniqueHash);
  }, [uniqueHash]);

  const handleGoBackToMerchant = useCallback(() => {
    if (!trustedMerchantReturnActionUrl || typeof window === 'undefined') {
      return;
    }

    if (isEmbedded) {
      try {
        if (window.top) {
          window.top.location.href = trustedMerchantReturnActionUrl;
          return;
        }
      } catch (navigationError) {
        console.warn('Could not redirect top frame to trusted merchant return URL', navigationError);
      }
    }

    window.location.assign(trustedMerchantReturnActionUrl);
  }, [isEmbedded, trustedMerchantReturnActionUrl]);

  const handleCopySwapDepositAddress = async () => {
    if (!swapExchange.depositAddress) {
      return;
    }

    try {
      await navigator.clipboard.writeText(swapExchange.depositAddress);
      setCopiedSwapDepositAddress(true);
      window.setTimeout(() => setCopiedSwapDepositAddress(false), 2000);
    } catch (clipboardError) {
      console.error('Failed to copy RizzSwap deposit address', clipboardError);
    }
  };

  const handleContinueWithProvider = useCallback(async (
    providerOption: PublicCheckoutOption,
    preparedPopup?: Window | null
  ) => {
    if (!uniqueHash || !providerOption) {
      setFiatError('Choose a provider first.');
      return;
    }

    setSelectedFiatSessionId(providerOption.sessionId);
    setLastAttemptedFiatProvider(providerOption);
    setFiatLaunchInProgress(true);
    setFiatError(null);

    const popup = preparedPopup === undefined
      ? openCheckoutWindow()
      : preparedPopup;
    try {
      const launch = await publicPaymentService.markFiatProviderIntent(
        uniqueHash,
        providerOption.sessionId,
        buyerContext
      );

      setStatus(launch.paymentStatus);
      setStatusMessage(`Continue in ${providerOption.provider}. This page will update automatically.`);

      const launchUrl = launch.launchUrl || providerOption.url || '';
      if (!launchUrl) {
        if (popup && !popup.closed) {
          popup.close();
        }
        setFiatError('Provider checkout URL is not available right now.');
        return;
      }

      if (popup && !popup.closed) {
        popup.location.replace(launchUrl);
      } else {
        const fallbackWindow = openCheckoutWindow(launchUrl);
        if (fallbackWindow && !fallbackWindow.closed) {
          fallbackWindow.location.replace(launchUrl);
        } else {
          setFiatError('Please allow pop-ups to continue with secure checkout.');
        }
      }
    } catch (providerError) {
      if (popup && !popup.closed) {
        popup.close();
      }
      console.error('Failed to continue with fiat provider', providerError);
      const errorMessage = extractApiErrorMessage(providerError, 'We could not open provider checkout right now.');
      setFiatError(errorMessage);

      if (shouldRefreshProviderOptionsAfterError(errorMessage)) {
        setStatusMessage('This provider is temporarily unavailable. Choose another available provider.');
        await reloadFiatOptionsForCurrentBuyer();
      }
    } finally {
      setFiatLaunchInProgress(false);
    }
  }, [buyerContext, reloadFiatOptionsForCurrentBuyer, uniqueHash]);

  const handleReportFiatProviderIssue = useCallback(async () => {
    if (!uniqueHash || !lastAttemptedFiatProvider) {
      return;
    }

    setFiatProviderIssueSubmitting(true);
    setFiatError(null);
    setStatusMessage(`Looking for another provider instead of ${lastAttemptedFiatProvider.provider}.`);

    const popup = openCheckoutWindow();
    try {
      const optionsResult = await publicPaymentService.reportFiatProviderIssue(
        uniqueHash,
        {
          sessionId: lastAttemptedFiatProvider.sessionId,
          provider: lastAttemptedFiatProvider.provider,
          paymentMethod: lastAttemptedFiatProvider.paymentMethod,
          reasonCode: 'payer_reported_provider_issue',
        },
        buyerContext
      );

      setBuyerContext({
        payerCountryCode: optionsResult.buyerCountryCode ?? buyerContext?.payerCountryCode ?? null,
        payerSubdivisionCode: optionsResult.buyerSubdivisionCode ?? buyerContext?.payerSubdivisionCode ?? null,
      });

      const nextOptions = chooseProviderOptions(optionsResult.fiatOptions ?? []);
      setFiatOptions(optionsResult.fiatOptions ?? []);

      const nextProvider = nextOptions.find(option => option.sessionId !== lastAttemptedFiatProvider.sessionId)
        ?? nextOptions[0]
        ?? null;

      if (!nextProvider) {
        if (popup && !popup.closed) {
          popup.close();
        }
        setSelectedFiatSessionId(null);
        setLastAttemptedFiatProvider(null);
        setStatusMessage(null);
        setFiatError('No alternative provider is available for this payment right now.');
        return;
      }

      setSelectedFiatSessionId(nextProvider.sessionId);
      setStatusMessage(`${lastAttemptedFiatProvider.provider} will not be shown again on this device. Opening ${nextProvider.provider}.`);
      await handleContinueWithProvider(nextProvider, popup);
    } catch (providerIssueError) {
      if (popup && !popup.closed) {
        popup.close();
      }
      console.error('Failed to report fiat provider issue', providerIssueError);
      setFiatError(extractApiErrorMessage(providerIssueError, 'We could not switch providers right now. Please try again.'));
    } finally {
      setFiatProviderIssueSubmitting(false);
    }
  }, [buyerContext, handleContinueWithProvider, lastAttemptedFiatProvider, uniqueHash]);

  const handleCreateSimpleSwapExchange = async () => {
    if (!uniqueHash || !paymentPage || !swapOption) {
      return;
    }

    if (!swapReady) {
      setSwapError(
        swapOption.unavailableReason
          || 'RizzSwap is not fully configured for this payment yet.'
      );
      return;
    }

    const tickerFrom = normalizeKey(swapFromTicker);
    const networkFrom = normalizeKey(swapFromNetwork);
    const amountText = swapAmount.trim().replace(',', '.');
    const amount = Number(amountText);

    if (!tickerFrom || !networkFrom) {
      setSwapError('Choose your source asset and network.');
      return;
    }

    if (!Number.isFinite(amount) || amount <= 0) {
      setSwapError('Could not calculate a valid source amount yet. Please pick another asset/network.');
      return;
    }

    const tickerTo = swapDestinationTicker;
    const networkTo = swapDestinationNetwork;
    if (!tickerTo || !networkTo) {
      setSwapError('Destination asset is not available from RizzSwap right now. Please refresh and try again.');
      return;
    }

    const exactReceiveAmount = `${paymentPage.quotedCryptoAmount ?? ''}`.trim();
    if (!exactReceiveAmount || !Number.isFinite(Number(exactReceiveAmount)) || Number(exactReceiveAmount) <= 0) {
      setSwapError('Could not determine required destination amount for this payment.');
      return;
    }

    setSwapCreating(true);
    setSwapError(null);
    try {
      try {
        const nextStatus = await publicPaymentService.markCryptoSwapIntent(uniqueHash);
        setStatus(nextStatus);
      } catch (intentError) {
        console.error('Failed to mark crypto swap intent', intentError);
      }

      const createEnvelope = await publicPaymentService.createSimpleSwapExchange(uniqueHash, {
        tickerFrom,
        networkFrom,
        tickerTo,
        networkTo,
        amount: exactReceiveAmount,
        addressTo: swapOption.destinationAddress,
        fixed: true,
        reverse: true,
        rateId: swapRateId,
      });

      const result = createEnvelope?.result as Record<string, unknown> | undefined;
      const publicId = readString(result?.publicId) || readString(result?.id) || null;
      const providerStatus = readString(result?.status);
      const redirectUrl = readString(result?.redirectUrl) || readString(result?.url);
      const depositAddress = readString(result?.addressFrom) || readString(result?.address_from);
      const depositAmount = parseUnknownString(
        result?.amountFrom ?? result?.amount_from ?? result?.depositAmount ?? result?.fromAmount
      );
      const depositTicker = normalizeKey(
        parseUnknownString(result?.tickerFrom ?? result?.ticker_from ?? result?.coinFrom ?? result?.currency_from)
      );
      const depositNetwork = normalizeSimpleSwapNetwork(
        parseUnknownString(result?.networkFrom ?? result?.network_from ?? result?.chainFrom ?? result?.chain_from)
      );

      setStatusMessage(null);
      setSwapExchange({
        publicId,
        status: providerStatus,
        redirectUrl,
        depositAddress,
        depositAmount,
        depositTicker,
        depositNetwork,
      });
    } catch (swapCreateError) {
      console.error('Failed to create RizzSwap exchange', swapCreateError);
      setSwapError(extractApiErrorMessage(swapCreateError, 'Could not create RizzSwap exchange right now.'));
    } finally {
      setSwapCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="pp-page pp-page--entry pp-page--loading-redesign">
        <Container className="pp-container">
          <div className="pp-rizzpay-brand">
            <img src={RIZZPAY_LOGO_PUBLIC_PATH} alt="RizzPay logo" />
            <div className="pp-rizzpay-brand-copy">
              <strong>RizzPay</strong>
              <small>{RIZZPAY_TAGLINE}</small>
            </div>
          </div>
          <div className="pp-state">
            <Spinner animation="border" />
            <div className="pp-state-copy">Loading secure payment page...</div>
          </div>
        </Container>
      </div>
    );
  }

  if (error || !paymentPage) {
    return (
      <div className="pp-page">
        <Container className="pp-container">
          <div className="pp-rizzpay-brand">
            <img src={RIZZPAY_LOGO_PUBLIC_PATH} alt="RizzPay logo" />
            <div className="pp-rizzpay-brand-copy">
              <strong>RizzPay</strong>
              <small>{RIZZPAY_TAGLINE}</small>
            </div>
          </div>
          <Alert variant="danger" className="mt-4">
            {error || 'Payment page not found.'}
          </Alert>
        </Container>
      </div>
    );
  }

  const methodForStatusPage = selectedMethodLabel;
  const providerForStatusPage = selectedProviderLabel;
  const showEntryMenu = !isFinal && !showSubmittedPage && !underpaidTopUpRequired && activeFlow === 'menu';
  const showStatusSurface = isFinal || showSubmittedPage || underpaidTopUpRequired;

  return (
    <div className={`pp-page${isEmbedded ? ' pp-page--embedded' : ''}${showEntryMenu ? ' pp-page--entry' : ''}${activeFlow === 'crypto' && !isFinal && !showSubmittedPage && !underpaidTopUpRequired ? ' pp-page--figma-crypto' : ''}${showStatusSurface ? ' pp-page--figma-status' : ''}`}>
      <Container className="pp-container">
        <div className="pp-rizzpay-brand">
          <img src={RIZZPAY_LOGO_PUBLIC_PATH} alt="RizzPay logo" />
          <div className="pp-rizzpay-brand-copy">
            <strong>RizzPay</strong>
            <small>{RIZZPAY_TAGLINE}</small>
          </div>
        </div>
        <Row className="pp-layout g-4">
          <Col xl={12}>
            {isSuccessFinal && (
              <Card className="pp-status-card pp-status-card--confirmed">
                <Card.Body>
                  <div className="pp-status-icon pp-status-icon--confirmed">✓</div>
                  <h2>Payment confirmed</h2>
                  <p>Thank you. Your payment has been successfully confirmed.</p>

                  <div className="pp-status-details">
                    <div><span>Order #</span><strong>{paymentReference}</strong></div>
                    <div><span>Amount paid</span><strong>{paymentAmountLabel}</strong></div>
                    <div><span>Payment method</span><strong>{methodForStatusPage}</strong></div>
                    <div><span>Provider</span><strong>{providerForStatusPage}</strong></div>
                    <div><span>Date</span><strong>{formatDateTime(status?.paidAt)}</strong></div>
                  </div>

                  {showMerchantReturnButton && (
                    <div className="pp-status-actions">
                      <Button variant="primary" onClick={handleGoBackToMerchant}>
                        Go back to merchant
                      </Button>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}

            {!isSuccessFinal && isFinal && (
              <Card className="pp-status-card pp-status-card--failed">
                <Card.Body>
                  <div className="pp-status-icon pp-status-icon--failed">!</div>
                  <h2>{isUnderpaidFinal ? 'Underpaid amount received' : 'Payment not completed'}</h2>
                  <p>{effectiveDisplayStatus}</p>
                  {isUnderpaidFinal && (
                    <div className="pp-status-details">
                      <div><span>Amount received</span><strong>{receivedAmountLabel || 'Not available'}</strong></div>
                      <div><span>Amount required</span><strong>{requiredCryptoAmountLabel || paymentAmountLabel}</strong></div>
                      <div><span>Still missing</span><strong>{underpaidMissingAmountLabel || 'Not available'}</strong></div>
                    </div>
                  )}
                  {isUnderpaidFinal && (
                    <div className="pp-status-note">
                      This payment has already been settled as underpaid. Please do not send extra funds to this address unless the merchant gives you a new payment link.
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}

            {!isFinal && underpaidTopUpRequired && (
              <Card className="pp-status-card pp-status-card--topup">
                <Card.Body>
                  <div className="pp-status-icon pp-status-icon--submitted">!</div>
                  <h2>Send the remaining amount</h2>
                  <p>
                    We detected your payment, but it is below the amount required to complete this checkout.
                  </p>

                  <div className="pp-status-details">
                    <div><span>Amount received</span><strong>{receivedAmountLabel || 'Not available'}</strong></div>
                    <div><span>Amount required</span><strong>{requiredCryptoAmountLabel || paymentAmountLabel}</strong></div>
                    <div><span>Still missing</span><strong>{underpaidMissingAmountLabel || 'Not available'}</strong></div>
                  </div>

                  <div className="pp-topup-box">
                    <div className="pp-topup-box__copy">
                      <span>Send exactly</span>
                      <strong>{underpaidMissingAmountLabel || 'Remaining amount'}</strong>
                      <small>Send {requiredCryptoCurrency || 'USDC'} on {paymentPage.cryptoNetwork || 'Polygon'} to the address below. This page will update when the payment is complete.</small>
                    </div>

                    {qrCodeDataUrl && (
                      <div className="pp-topup-box__qr">
                        <img src={qrCodeDataUrl} alt="Remaining payment QR code" />
                      </div>
                    )}
                  </div>

                  <div className="pp-wallet-box pp-wallet-box--topup">
                    {paymentPage.uniqueWalletAddress}
                  </div>

                  <div className="pp-button-row pp-button-row--compact pp-topup-actions">
                    <Button variant="primary" onClick={handleCopyTopUpAmount}>
                      {copiedTopUpAmount ? 'Amount copied' : 'Copy amount'}
                    </Button>
                    <Button variant="outline-secondary" onClick={handleCopyWallet}>
                      {copiedWallet ? 'Wallet copied' : 'Copy wallet'}
                    </Button>
                    <Button variant="outline-secondary" onClick={openExplorer}>
                      View in explorer
                    </Button>
                  </div>

                  <PaymentProgressTimeline steps={submittedPaymentProgressSteps} />

                  <div className="pp-status-note">
                    Your payment is not complete yet. Do not close this page until the remaining amount is received and approved.
                  </div>

                  <Button variant="outline-secondary" className="pp-status-refresh-btn" onClick={refreshStatus}>
                    Refresh status
                  </Button>
                </Card.Body>
              </Card>
            )}

            {!isFinal && swapUnderpaymentReviewRequired && (
              <Card className="pp-status-card pp-status-card--swap-review">
                <Card.Body>
                  <div className="pp-status-icon pp-status-icon--failed">!</div>
                  <h2>Swap payment needs review</h2>
                  <p>
                    The swap provider delivered less USDC than this checkout requires.
                  </p>

                  <div className="pp-status-details">
                    <div><span>Amount received</span><strong>{receivedAmountLabel || 'Not available'}</strong></div>
                    <div><span>Amount required</span><strong>{requiredCryptoAmountLabel || paymentAmountLabel}</strong></div>
                    <div><span>Still missing</span><strong>{underpaidMissingAmountLabel || 'Not available'}</strong></div>
                  </div>

                  <div className="pp-status-note pp-status-note--warning">
                    Do not send more funds to the swap provider deposit address. This payment needs merchant or support review.
                  </div>

                  <Button variant="outline-secondary" className="pp-status-refresh-btn" onClick={refreshStatus}>
                    Refresh status
                  </Button>
                </Card.Body>
              </Card>
            )}

            {!isFinal && showSubmittedPage && (
              <Card className="pp-status-card pp-status-card--submitted">
                <Card.Body>
                  <div className="pp-status-icon pp-status-icon--submitted">⏱</div>
                  <h2>Payment submitted</h2>
                  <p>Waiting for secure payment confirmation...</p>

                  <div className="pp-status-details">
                    <div><span>Order #</span><strong>{paymentReference}</strong></div>
                    <div><span>Amount</span><strong>{paymentAmountLabel}</strong></div>
                    <div><span>Payment method</span><strong>{methodForStatusPage}</strong></div>
                    <div><span>Provider</span><strong>{providerForStatusPage}</strong></div>
                  </div>

                  <PaymentProgressTimeline steps={submittedPaymentProgressSteps} />

                  <div className="pp-status-note">
                    This page updates automatically once payment is confirmed.
                  </div>

                  <Button variant="outline-secondary" className="pp-status-refresh-btn" onClick={refreshStatus}>
                    Refresh status
                  </Button>
                </Card.Body>
              </Card>
            )}

            {!isFinal && !showSubmittedPage && !underpaidTopUpRequired && activeFlow === 'menu' && (
              <Card className="pp-flow-card pp-flow-card--entry">
                <Card.Body>
                  {paymentPage.isInvoice && (
                    <>
                      <div className="pp-invoice-action-bar">
                        <div className="pp-invoice-action-buttons">
                          <Button variant="outline-secondary" onClick={handlePreviewInvoice}>
                            Preview invoice
                          </Button>
                          <Button variant="outline-secondary" onClick={handleDownloadInvoice}>
                            Download invoice
                          </Button>
                          <Button variant="outline-secondary" onClick={handlePrintInvoice}>
                            Print invoice
                          </Button>
                        </div>
                      </div>

                      <div className="pp-invoice-summary-grid">
                        <div className="pp-invoice-summary-item pp-invoice-summary-item--reference">
                          <span>Invoice number</span>
                          <strong>{invoiceDetails?.invoiceNumber?.trim() || paymentReference}</strong>
                        </div>
                        <div className="pp-invoice-summary-item">
                          <span>Invoice amount</span>
                          <strong>{invoiceAmountLabel}</strong>
                        </div>
                        <div className="pp-invoice-summary-item">
                          <span>Issued by</span>
                          <strong>{invoiceIssuerCompanyName}</strong>
                        </div>
                        <div className="pp-invoice-summary-item">
                          <span>Issued to</span>
                          <strong>{invoicePayingCompanyName}</strong>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="pp-entry-summary">
                    <div>
                      <span>Paying to</span>
                      <strong>{publicMerchantName}</strong>
                    </div>
                    <div>
                      <span>Amount</span>
                      <strong>{paymentAmountHeroLabel}</strong>
                    </div>
                  </div>

                  <div className="pp-entry-heading">
                    <h2>How would you like to pay?</h2>
                    <p>Choose a method - USDC lands in the business wallet within minutes.</p>
                  </div>

                  <div className="pp-entry-methods">
                    <button
                      type="button"
                      className={`pp-entry-method pp-entry-method--fiat${fiatMethodDisabled ? ' pp-entry-method--disabled' : ''}`}
                      onClick={() => void startFiatFlow()}
                      disabled={fiatMethodDisabled}
                    >
                      <span className="pp-entry-method__icon">
                        <img src={methodCreditCardIcon} alt="" />
                      </span>
                      <span className="pp-entry-method__body">
                        <span className="pp-entry-method__eyebrow">Buy USDC</span>
                        <strong>Card / Bank / Apple Pay</strong>
                        <small>{fiatMethodDescription}</small>
                        {fiatMethodUnavailable && (
                          <span className="pp-entry-method__state">Not yet available in your country</span>
                        )}
                      </span>
                    </button>

                    <button
                      type="button"
                      className="pp-entry-method pp-entry-method--crypto"
                      onClick={startCryptoFlow}
                    >
                      <span className="pp-entry-method__icon">
                        <img src={USDC_ICON_URL} alt="" />
                      </span>
                      <span className="pp-entry-method__body">
                        <span className="pp-entry-method__eyebrow">Pay with crypto</span>
                        <strong>Send from any wallet</strong>
                        <small>USDC, BTC, ETH and 100+ more</small>
                      </span>
                    </button>
                  </div>

                  {fiatError && (
                    <Alert variant="danger" className="mt-3">
                      {fiatError}
                    </Alert>
                  )}

                  {lastAttemptedFiatProvider && !fiatMethodUnavailable && (
                    <div className="pp-fiat-recovery">
                      <div>
                        <strong>Having trouble with {lastAttemptedFiatProvider.provider}?</strong>
                        <span>We can stop showing this provider on this device and try the next available option.</span>
                      </div>
                      <Button
                        type="button"
                        variant="outline-light"
                        className="pp-fiat-recovery__button"
                        onClick={() => void handleReportFiatProviderIssue()}
                        disabled={fiatProviderIssueSubmitting || fiatLaunchInProgress}
                      >
                        {fiatProviderIssueSubmitting ? (
                          <>
                            <Spinner size="sm" animation="border" />
                            Switching
                          </>
                        ) : (
                          'Try another provider'
                        )}
                      </Button>
                    </div>
                  )}

                  <div className="pp-entry-disclaimer">
                    By selecting Buy USDC you agree to the third-party provider's Terms of Service and their KYC process. Keep this page open until your payment is confirmed.
                  </div>
                </Card.Body>
              </Card>
            )}

            {!isFinal && !showSubmittedPage && !underpaidTopUpRequired && activeFlow === 'crypto' && (
              <Card className="pp-flow-card pp-flow-card--crypto-unified">
                <Card.Body>
                  <div className="pp-flow-top">
                    <Button variant="outline-secondary" className="pp-back-button" onClick={goToMethodMenu}>
                      Back to payment methods
                    </Button>
                    <h2>Pay with crypto</h2>
                    <p>Send USDC directly, or choose another crypto and we will swap it into USDC for this payment.</p>
                  </div>

                  <div className="pp-unified-crypto-grid">
                    <div className="pp-surface-card pp-unified-direct-card">
                      <div className="pp-panel-title">USDC (POL)</div>
                      <p className="pp-panel-copy">
                        Send USDC on Polygon from any wallet.
                      </p>

                      <div className="pp-crypto-amount">
                        {paymentPage.quotedCryptoAmount
                          ? `${formatAmount(paymentPage.quotedCryptoAmount, paymentPage.cryptoCurrency)} ${paymentPage.cryptoCurrency}`
                          : `Loading ${paymentPage.cryptoCurrency} quote`}
                      </div>
                      <div className="pp-chip-row pp-chip-row--icons" aria-label={`${paymentPage.cryptoCurrency} on ${paymentPage.cryptoNetwork}`}>
                        <span title={paymentPage.cryptoCurrency}>
                          <img src={directCryptoIconUrl} alt={paymentPage.cryptoCurrency} />
                        </span>
                        {directNetworkIconUrl && (
                          <span title={paymentPage.cryptoNetwork}>
                            <img src={directNetworkIconUrl} alt={paymentPage.cryptoNetwork} />
                          </span>
                        )}
                      </div>

                      {qrCodeDataUrl && (
                        <div className="pp-qr-wrap">
                          <span>Scan to pay</span>
                          <img src={qrCodeDataUrl} alt="Payment QR code" />
                        </div>
                      )}

                      <PaymentProgressTimeline steps={directActiveProgressSteps} />

                      <div className="pp-wallet-box">
                        {paymentPage.uniqueWalletAddress}
                      </div>

                      <div className="pp-button-row pp-button-row--compact">
                        <Button variant="primary" onClick={handleCopyWallet}>
                          {copiedWallet ? 'Wallet copied' : 'Copy wallet'}
                        </Button>
                        <Button variant="outline-secondary" onClick={openExplorer}>
                          View in explorer
                        </Button>
                      </div>

                      {quoteCountdown && (
                        <div className="pp-inline-note">
                          Quote window: <strong>{quoteCountdown}</strong>
                        </div>
                      )}
                    </div>

                    <div className="pp-surface-card pp-unified-swap-card">
                      <div className="pp-panel-title">Pay with any coin</div>
                      <p className="pp-panel-copy">
                        Choose the asset you want to send.
                      </p>

                      {swapError && (
                        <Alert variant="danger">
                          {swapError}
                        </Alert>
                      )}

                      {!swapReady && (
                        <Alert variant="warning">
                          {swapOption?.unavailableReason || 'RizzSwap is not available for this checkout right now.'}
                        </Alert>
                      )}

                      {swapReady && (
                        <>
                          <div className="pp-swap-widget">
                            <div className="pp-swap-widget__row">
                              <div className="pp-swap-widget__amount">
                                <span>You send</span>
                                <strong>{swapAmount || (swapEstimating ? 'Calculating...' : 'Select asset')}</strong>
                              </div>

                              <div className="pp-picker pp-swap-widget__token" onClick={event => event.stopPropagation()}>
                                <button
                                  type="button"
                                  className="pp-swap-token-trigger"
                                  disabled={swapCurrenciesLoading || swapCreating}
                                  onClick={() => {
                                    setSwapTickerPickerOpen(open => !open);
                                    setSwapNetworkPickerOpen(false);
                                  }}
                                >
                                  {selectedSwapAssetNetworkOption?.imageUrl ? (
                                    <img
                                      src={selectedSwapAssetNetworkOption.imageUrl}
                                      alt={selectedSwapAssetNetworkOption.symbol.toUpperCase()}
                                    />
                                  ) : (
                                    <span className="pp-picker-trigger-fallback">
                                      {(selectedSwapAssetNetworkOption?.symbol || '?').slice(0, 1).toUpperCase()}
                                    </span>
                                  )}
                                  <span>{(selectedSwapAssetNetworkOption?.symbol || 'Asset').toUpperCase()}</span>
                                  <span className="pp-swap-token-trigger__chevron" aria-hidden="true" />
                                </button>

                                {swapTickerPickerOpen && (
                                  <div className="pp-picker-menu pp-picker-menu--widget">
                                    <input
                                      className="pp-picker-search"
                                      type="text"
                                      placeholder="Search coin or network..."
                                      value={swapTickerSearch}
                                      onChange={event => setSwapTickerSearch(event.target.value)}
                                    />
                                    {!swapTickerSearch.trim() && swapFeaturedRouteOptions.length > 0 && (
                                      <div className="pp-route-shortcuts">
                                        <div className="pp-picker-section-title">Popular</div>
                                        <div className="pp-route-shortcut-grid">
                                          {swapFeaturedRouteOptions.map(option => (
                                            <button
                                              key={`featured-${option.key}`}
                                              type="button"
                                              className={`pp-route-shortcut${swapFromTicker === option.symbol && swapFromNetwork === option.network ? ' pp-route-shortcut--active' : ''}`}
                                              onClick={() => selectSwapAssetNetworkOption(option)}
                                            >
                                              {option.imageUrl ? (
                                                <img src={option.imageUrl} alt={option.symbol.toUpperCase()} />
                                              ) : (
                                                <span className="pp-picker-option-fallback">{option.symbol.slice(0, 1).toUpperCase()}</span>
                                              )}
                                              <span>{option.symbol.toUpperCase()}</span>
                                              <small>{option.networkLabel}</small>
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {swapRouteNetworkFilters.length > 0 && (
                                      <div className="pp-route-network-filter">
                                        <div className="pp-picker-section-title">Network</div>
                                        <div className="pp-route-network-scroll">
                                          <button
                                            type="button"
                                            className={`pp-route-network-chip${swapRouteNetworkFilter === 'all' ? ' pp-route-network-chip--active' : ''}`}
                                            onClick={() => setSwapRouteNetworkFilter('all')}
                                          >
                                            All
                                          </button>
                                          {swapRouteNetworkFilters.map(option => (
                                            <button
                                              key={option.key}
                                              type="button"
                                              className={`pp-route-network-chip${swapRouteNetworkFilter === option.key ? ' pp-route-network-chip--active' : ''}`}
                                              onClick={() => setSwapRouteNetworkFilter(option.key)}
                                            >
                                              {option.iconUrl && <img src={option.iconUrl} alt={option.label} />}
                                              <span>{option.label}</span>
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    <div className="pp-picker-options">
                                      {filteredSwapAssetNetworkOptions.length === 0 && (
                                        <div className="pp-picker-empty">No routes found</div>
                                      )}
                                      {filteredSwapAssetNetworkOptions.map(option => (
                                        <button
                                          key={option.key}
                                          type="button"
                                          className={`pp-picker-option${swapFromTicker === option.symbol && swapFromNetwork === option.network ? ' pp-picker-option--active' : ''}`}
                                          onClick={() => selectSwapAssetNetworkOption(option)}
                                        >
                                          <span className="pp-picker-option-icons">
                                            {option.imageUrl ? (
                                              <img className="pp-picker-option-asset-icon" src={option.imageUrl} alt={option.symbol.toUpperCase()} />
                                            ) : (
                                              <span className="pp-picker-option-fallback">{option.symbol.slice(0, 1).toUpperCase()}</span>
                                            )}
                                            {option.iconUrl && (
                                              <img className="pp-picker-option-network-icon" src={option.iconUrl} alt={option.networkLabel} />
                                            )}
                                          </span>
                                          <span className="pp-picker-option-copy">
                                            <strong>{option.symbol.toUpperCase()}</strong>
                                            <small>{option.displayName || option.label}</small>
                                          </span>
                                          <span className="pp-picker-network-pill">{option.networkLabel}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="pp-swap-widget__address">
                              <span>The recipient's {paymentPage.cryptoCurrency} ({paymentPage.cryptoNetwork}) address</span>
                              <strong>{swapOption?.destinationAddress || paymentPage.uniqueWalletAddress}</strong>
                            </div>

                            <div className="pp-swap-meta">
                              {swapCurrenciesLoading && <span>Loading RizzSwap assets...</span>}
                              {!swapCurrenciesLoading && swapTickerOptions.length === 0 && (
                                <span>No assets available from RizzSwap right now. Please refresh in a moment.</span>
                              )}
                              {!swapCurrenciesLoading && swapEstimating && (
                                <span>Preparing payment route...</span>
                              )}
                            </div>

                            {!swapExchange.publicId && (
                              <Button
                                className="pp-swap-create-btn"
                                variant="primary"
                                disabled={swapCreating || swapEstimating || !swapAmount || swapCurrenciesLoading}
                                onClick={() => void handleCreateSimpleSwapExchange()}
                              >
                                {swapCreating ? 'Creating payment...' : 'Pay'}
                              </Button>
                            )}

                            {swapExchange.publicId && (
                              <div className="pp-swap-widget__deposit">
                                <div className="pp-inline-note pp-inline-note--swap">
                                  Send your payment to the address below, The business will receives USDC.
                                </div>

                                <div className="pp-swap-widget__deposit-head">
                                  <div>
                                    <span>Pending deposit</span>
                                    <strong>Exchange ID: {swapExchange.publicId}</strong>
                                  </div>
                                  <small>{swapExchange.status || 'Created'}</small>
                                </div>

                                <div className="pp-swap-widget__deposit-amount">
                                  <span>Send deposit</span>
                                  <strong>
                                    {(swapExchange.depositAmount || swapAmount || '...')}
                                    {' '}
                                    {(swapExchange.depositTicker || swapFromTicker || '').toUpperCase()}
                                    {' on '}
                                    {(swapExchange.depositNetwork || swapFromNetwork || '').toUpperCase()}
                                  </strong>
                                </div>

                                {swapExchange.depositAddress && (
                                  <div className="pp-swap-widget__deposit-address">
                                    {swapDepositQrCodeDataUrl && (
                                      <img src={swapDepositQrCodeDataUrl} alt="RizzSwap deposit QR code" />
                                    )}
                                    <div>
                                      <span>Exchange deposit address</span>
                                      <strong>{swapExchange.depositAddress}</strong>
                                      <div className="pp-button-row pp-button-row--compact">
                                        <Button variant="outline-secondary" onClick={handleCopySwapDepositAddress}>
                                          {copiedSwapDepositAddress ? 'Address copied' : 'Copy address'}
                                        </Button>
                                        {swapExchange.redirectUrl && (
                                          <a href={swapExchange.redirectUrl} target="_blank" rel="noopener noreferrer">
                                            Open provider page
                                          </a>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                <PaymentProgressTimeline steps={swapActiveProgressSteps} />

                                <div className="pp-inline-note pp-inline-note--swap">
                                  Send only the exact amount above. Keep this page open while we confirm the payment.
                                </div>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="pp-inline-note">
                    The business receives USDC either way. Keep this page open until the payment status updates.
                  </div>
                </Card.Body>
              </Card>
            )}

            {!isFinal && !showSubmittedPage && !underpaidTopUpRequired && activeFlow === 'direct' && (
              <Card className="pp-flow-card">
                <Card.Body>
                  <div className="pp-flow-top">
                    <Button variant="outline-secondary" className="pp-back-button" onClick={goToCryptoOptions}>
                      Back to crypto options
                    </Button>
                    <h2>Pay by wallet</h2>
                    <p>Send {paymentPage.cryptoCurrency} on {paymentPage.cryptoNetwork}, then keep this page open while we confirm it.</p>
                  </div>

                  <div className="pp-direct-grid">
                    <div className="pp-surface-card">
                      <div className="pp-mini-label">Pay this amount</div>
                      <div className="pp-crypto-amount">
                        {paymentPage.quotedCryptoAmount
                          ? `${formatAmount(paymentPage.quotedCryptoAmount, paymentPage.cryptoCurrency)} ${paymentPage.cryptoCurrency}`
                          : `Loading ${paymentPage.cryptoCurrency} quote`}
                      </div>
                      <div className="pp-chip-row">
                        <span>{paymentPage.cryptoCurrency}</span>
                        <span>{paymentPage.cryptoNetwork}</span>
                      </div>

                      <div className="pp-step-list">
                        <div><strong>1</strong><span>Copy the wallet or scan the QR code</span></div>
                        <div><strong>2</strong><span>Send payment from your wallet</span></div>
                        <div><strong>3</strong><span>Wait here for live confirmation</span></div>
                      </div>
                    </div>

                    <div className="pp-surface-card">
                      <div className="pp-mini-label">Payment details</div>
                      <div className="pp-panel-title">One-time wallet</div>
                      <p className="pp-panel-copy">
                        Use this wallet only for this payment. The status on this page stays live while funds are confirmed.
                      </p>

                      {qrCodeDataUrl && (
                        <div className="pp-qr-wrap">
                          <span>Scan to pay</span>
                          <img src={qrCodeDataUrl} alt="Payment QR code" />
                        </div>
                      )}

                      <PaymentProgressTimeline steps={directActiveProgressSteps} />

                      <div className="pp-wallet-box">
                        {paymentPage.uniqueWalletAddress}
                      </div>

                      <div className="pp-button-row">
                        <Button variant="primary" onClick={handleCopyWallet}>
                          {copiedWallet ? 'Wallet copied' : 'Copy wallet'}
                        </Button>
                        <Button variant="outline-secondary" onClick={openExplorer}>
                          View in explorer
                        </Button>
                      </div>

                      {quoteCountdown && (
                        <div className="pp-inline-note">
                          Current amount window: <strong>{quoteCountdown}</strong>. Small timing differences are reviewed before settlement.
                        </div>
                      )}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}

            {!isFinal && !showSubmittedPage && !underpaidTopUpRequired && activeFlow === 'swap' && (
              <Card className="pp-flow-card">
                <Card.Body>
                  <div className="pp-flow-top">
                    <Button variant="outline-secondary" className="pp-back-button" onClick={goToCryptoOptions}>
                      Back to crypto options
                    </Button>
                    <h2>Swap crypto</h2>
                    <p>Create a secure RizzSwap exchange pre-locked to this payment destination.</p>
                  </div>

                  {swapError && (
                    <Alert variant="danger">
                      {swapError}
                    </Alert>
                  )}

                  {!swapReady && (
                    <Alert variant="warning">
                      {swapOption?.unavailableReason || 'RizzSwap is not available for this checkout right now.'}
                    </Alert>
                  )}

                  {swapReady && (
                    <>
                      <div className="pp-swap-grid">
                        <div className="pp-surface-card">
                          <div className="pp-mini-label">This payment arrives as</div>
                          <div className="pp-panel-title">{paymentPage.cryptoCurrency} on {paymentPage.cryptoNetwork}</div>

                          <div className="pp-mini-label mt-3">Pay this amount right now</div>
                          <div className="pp-crypto-amount">
                            {paymentPage.quotedCryptoAmount
                              ? `${formatAmount(paymentPage.quotedCryptoAmount, paymentPage.cryptoCurrency)} ${paymentPage.cryptoCurrency}`
                              : `Loading ${paymentPage.cryptoCurrency} quote`}
                          </div>
                          {quoteCountdown && (
                            <div className="pp-inline-note">{quoteCountdown}</div>
                          )}
                        </div>

                        <div className="pp-surface-card">
                          <div className="pp-mini-label">Secure exchange</div>
                          <div className="pp-panel-title">Create RizzSwap exchange</div>
                          <p className="pp-panel-copy">
                            Enter the source asset you want to pay with. Destination stays locked to this checkout.
                          </p>

                          <div className="pp-form-grid">
                            <label className="pp-picker-field">
                              <span>Source asset ticker</span>
                              <div className="pp-picker" onClick={event => event.stopPropagation()}>
                                <button
                                  type="button"
                                  className="pp-picker-trigger"
                                  disabled={swapCurrenciesLoading || swapCreating}
                                  onClick={() => {
                                    setSwapTickerPickerOpen(open => !open);
                                    setSwapNetworkPickerOpen(false);
                                  }}
                                >
                                  <span className="pp-picker-trigger-main">
                                    {selectedSwapTickerOption?.imageUrl ? (
                                      <img src={selectedSwapTickerOption.imageUrl} alt={selectedSwapTickerOption.symbol.toUpperCase()} />
                                    ) : (
                                      <span className="pp-picker-trigger-fallback">
                                        {(selectedSwapTickerOption?.symbol || '?').slice(0, 1).toUpperCase()}
                                      </span>
                                    )}
                                    <span>{selectedSwapTickerOption?.label || 'Select asset'}</span>
                                  </span>
                                  <span className="pp-picker-caret">▾</span>
                                </button>

                                {swapTickerPickerOpen && (
                                  <div className="pp-picker-menu">
                                    <input
                                      className="pp-picker-search"
                                      type="text"
                                      placeholder="Search asset..."
                                      value={swapTickerSearch}
                                      onChange={event => setSwapTickerSearch(event.target.value)}
                                    />
                                    <div className="pp-picker-options">
                                      {filteredSwapTickerOptions.length === 0 && (
                                        <div className="pp-picker-empty">No assets found</div>
                                      )}
                                      {filteredSwapTickerOptions.map(option => (
                                        <button
                                          key={option.symbol}
                                          type="button"
                                          className={`pp-picker-option${swapFromTicker === option.symbol ? ' pp-picker-option--active' : ''}`}
                                          onClick={() => {
                                            setSwapFromTicker(option.symbol);
                                            setSwapTickerPickerOpen(false);
                                          }}
                                        >
                                          {option.imageUrl ? (
                                            <img src={option.imageUrl} alt={option.symbol.toUpperCase()} />
                                          ) : (
                                            <span className="pp-picker-option-fallback">{option.symbol.slice(0, 1).toUpperCase()}</span>
                                          )}
                                          <span>{option.label}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </label>

                            <label className="pp-picker-field">
                              <span>Source network</span>
                              <div className="pp-picker" onClick={event => event.stopPropagation()}>
                                <button
                                  type="button"
                                  className="pp-picker-trigger"
                                  disabled={swapCurrenciesLoading || swapCreating || swapNetworkOptions.length === 0}
                                  onClick={() => {
                                    setSwapNetworkPickerOpen(open => !open);
                                    setSwapTickerPickerOpen(false);
                                  }}
                                >
                                  <span className="pp-picker-trigger-main">
                                    {selectedSwapNetworkOption?.iconUrl ? (
                                      <img
                                        src={selectedSwapNetworkOption.iconUrl}
                                        alt={selectedSwapNetworkOption.label}
                                        onError={event => {
                                          event.currentTarget.style.display = 'none';
                                        }}
                                      />
                                    ) : (
                                      <span className="pp-picker-option-fallback">
                                        {(selectedSwapNetworkOption?.label || '?').slice(0, 1).toUpperCase()}
                                      </span>
                                    )}
                                    <span className="pp-picker-network-pill">{selectedSwapNetworkOption?.label || 'No networks'}</span>
                                  </span>
                                  <span className="pp-picker-caret">▾</span>
                                </button>

                                {swapNetworkPickerOpen && (
                                  <div className="pp-picker-menu">
                                    <div className="pp-picker-options">
                                      {swapNetworkOptions.length === 0 && (
                                        <div className="pp-picker-empty">No networks</div>
                                      )}
                                      {swapNetworkOptions.map(option => (
                                        <button
                                          key={`${swapFromTicker}-${option.network}`}
                                          type="button"
                                          className={`pp-picker-option${swapFromNetwork === option.network ? ' pp-picker-option--active' : ''}`}
                                          onClick={() => {
                                            setSwapFromNetwork(option.network);
                                            setSwapNetworkPickerOpen(false);
                                          }}
                                        >
                                          {option.iconUrl ? (
                                            <img
                                              src={option.iconUrl}
                                              alt={option.label}
                                              onError={event => {
                                                event.currentTarget.style.display = 'none';
                                              }}
                                            />
                                          ) : (
                                            <span className="pp-picker-option-fallback">{option.label.slice(0, 1).toUpperCase()}</span>
                                          )}
                                          <span className="pp-picker-network-pill">{option.label}</span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </label>

                            <label>
                              <span>Amount to send</span>
                              <input
                                type="text"
                                value={swapAmount}
                                readOnly
                                placeholder="Calculating..."
                              />
                            </label>
                          </div>

                          <div className="pp-swap-meta">
                            {swapCurrenciesLoading && <span>Loading RizzSwap assets...</span>}
                            {!swapCurrenciesLoading && swapTickerOptions.length === 0 && (
                              <span>No assets available from RizzSwap right now. Please refresh in a moment.</span>
                            )}
                            {!swapCurrenciesLoading && swapEstimating && <span>Calculating exact send amount...</span>}
                            {!swapCurrenciesLoading && !swapEstimating && swapAmount && (
                              <span>
                                Calculated for ~{formatAmount(paymentPage.quotedCryptoAmount || 0, paymentPage.cryptoCurrency)} {paymentPage.cryptoCurrency} receive.
                              </span>
                            )}
                          </div>

                          <div className="pp-inline-note">
                            Destination is locked to <strong>{paymentPage.cryptoCurrency} on {paymentPage.cryptoNetwork}</strong> ({swapOption?.destinationAddress}).
                          </div>

                          <Button
                            className="pp-swap-create-btn"
                            variant="primary"
                            disabled={swapCreating || swapEstimating || !swapAmount || swapCurrenciesLoading}
                            onClick={() => void handleCreateSimpleSwapExchange()}
                          >
                            {swapCreating ? 'Creating...' : 'Create RizzSwap exchange'}
                          </Button>
                        </div>
                      </div>

                      {swapExchange.publicId && (
                        <div className="pp-surface-card pp-swap-deposit-stage mt-3">
                          <div className="pp-panel-title">Awaiting your payment</div>
                          <p className="pp-panel-copy">
                            Send the exact deposit amount shown below to the RizzSwap deposit address, then keep this page open while we take care of the rest.
                          </p>
                          <p className="pp-panel-copy pp-panel-copy--warning">
                            * Important! If an amount lower than the amount below is sent, the payment will fail and might cause a loss of the crypto that was sent.
                          </p>
                          <p className="pp-panel-copy pp-panel-copy--warning">
                            *Swap payment may take up to 25 min to be completed.
                          </p>
                          <div className="pp-status-row">
                            <div><span>Exchange ID</span><strong>{swapExchange.publicId}</strong></div>
                            <div><span>Provider status</span><strong>{swapExchange.status || 'Created'}</strong></div>
                          </div>
                          <div className="pp-status-row">
                            <div>
                              <span>Send deposit</span>
                              <strong>
                                {(swapExchange.depositAmount || swapAmount || '...')}
                                {' '}
                                {(swapExchange.depositTicker || swapFromTicker || '').toUpperCase()}
                              </strong>
                            </div>
                            <div>
                              <span>Network</span>
                              <strong>{(swapExchange.depositNetwork || swapFromNetwork || '').toUpperCase()}</strong>
                            </div>
                          </div>

                          {swapExchange.depositAddress && (
                            <div className="pp-swap-deposit-box">
                              {swapDepositQrCodeDataUrl && (
                                <img src={swapDepositQrCodeDataUrl} alt="RizzSwap deposit QR code" />
                              )}
                              <div className="pp-swap-deposit-box__right">
                                <div className="pp-mini-label">Deposit address</div>
                                <div className="pp-wallet-box">{swapExchange.depositAddress}</div>
                                <div className="pp-button-row">
                                  <Button variant="outline-secondary" onClick={handleCopySwapDepositAddress}>
                                    {copiedSwapDepositAddress ? 'Address copied' : 'Copy address'}
                                  </Button>
                                  {swapExchange.redirectUrl && (
                                    <a href={swapExchange.redirectUrl} target="_blank" rel="noopener noreferrer">
                                      Open provider page
                                    </a>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          <PaymentProgressTimeline steps={swapDepositProgressSteps} />
                        </div>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            )}

            {paymentPage.isInvoice && (
              <Modal
                show={showInvoicePreview}
                onHide={() => setShowInvoicePreview(false)}
                centered
                size="lg"
                dialogClassName="pp-invoice-preview-modal"
              >
                <Modal.Header closeButton>
                  <Modal.Title>Invoice preview</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <div className="pp-invoice-preview">
                    <div className="pp-rizzpay-brand pp-rizzpay-brand--invoice-preview">
                      <img src={RIZZPAY_LOGO_PUBLIC_PATH} alt="RizzPay logo" />
                      <div className="pp-rizzpay-brand-copy">
                        <strong>RizzPay</strong>
                        <small>{RIZZPAY_TAGLINE}</small>
                      </div>
                    </div>
                    <div className="pp-invoice-preview-head">
                      <div>
                        <div className="pp-mini-label">Invoice reference</div>
                        <h3>{paymentReference}</h3>
                      </div>
                      <div className="pp-invoice-preview-total">{invoiceAmountLabel}</div>
                    </div>

                    <div className="pp-invoice-preview-grid">
                      <div>
                        <span>Issue date</span>
                        <strong>{invoiceDetails?.issueDate ? formatDateTime(invoiceDetails.issueDate) : 'Not set'}</strong>
                      </div>
                      <div>
                        <span>Due date</span>
                        <strong>{invoiceDetails?.dueDate ? formatDateTime(invoiceDetails.dueDate) : 'Not set'}</strong>
                      </div>
                      <div>
                        <span>Issued by</span>
                        <strong>{invoiceIssuerCompanyName}</strong>
                      </div>
                      <div>
                        <span>Issued to</span>
                        <strong>{invoicePayingCompanyName}</strong>
                      </div>
                    </div>

                    <div className="pp-invoice-preview-wallet">
                      <div className="pp-mini-label">Destination wallet</div>
                      <div className="pp-panel-copy">
                        Send {paymentPage.cryptoCurrency} on {paymentPage.cryptoNetwork} to this one-time address.
                      </div>
                      <div className="pp-invoice-preview-wallet-grid">
                        {qrCodeDataUrl && <img src={qrCodeDataUrl} alt="Invoice payment QR code" />}
                        <div className="pp-wallet-box">{paymentPage.uniqueWalletAddress}</div>
                      </div>
                    </div>

                    <div className="pp-invoice-preview-table-wrap">
                      <table className="pp-invoice-preview-table">
                        <thead>
                          <tr>
                            <th>Item</th>
                            <th>Qty</th>
                            <th>Unit price</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceItems.length === 0 ? (
                            <tr>
                              <td colSpan={4}>No line items available.</td>
                            </tr>
                          ) : (
                            invoiceItems.map((item, index) => (
                              <tr key={`${item.itemName}-${index}`}>
                                <td>{item.itemName}</td>
                                <td>{item.quantity}</td>
                                <td>{formatAmount(item.unitPrice, paymentPage.fiatCurrency)} {paymentPage.fiatCurrency}</td>
                                <td>{formatAmount(item.amount, paymentPage.fiatCurrency)} {paymentPage.fiatCurrency}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="pp-invoice-preview-totals">
                      <div><span>Subtotal</span><strong>{formatAmount(invoiceSubtotal, paymentPage.fiatCurrency)} {paymentPage.fiatCurrency}</strong></div>
                      <div><span>Tax</span><strong>{formatAmount(invoiceTaxAmount, paymentPage.fiatCurrency)} {paymentPage.fiatCurrency}</strong></div>
                      <div><span>Total</span><strong>{invoiceAmountLabel}</strong></div>
                    </div>

                    {invoiceDetails?.notes?.trim() && (
                      <div className="pp-inline-note">
                        <strong>Notes:</strong> {invoiceDetails.notes.trim()}
                      </div>
                    )}
                  </div>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="outline-secondary" onClick={handleDownloadInvoice}>
                    Download invoice
                  </Button>
                  <Button variant="outline-secondary" onClick={handlePrintInvoice}>
                    Print invoice
                  </Button>
                  <Button variant="primary" onClick={() => setShowInvoicePreview(false)}>
                    Close
                  </Button>
                </Modal.Footer>
              </Modal>
            )}

            {statusMessage && !isFinal && !showSubmittedPage && (
              <Alert variant="info" className="mt-3">
                {statusMessage}
              </Alert>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PublicPaymentPageView;
